import * as React from 'react';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Zoom from '@mui/material/Zoom';
import AliceCarousel from 'react-alice-carousel';
import Cookies from 'js-cookie';
import 'react-alice-carousel/lib/alice-carousel.css';
// import { autoPlay } from 'react-swipeable-views-utils';
import { getCookie, getBaseUrl, getCloudfront, getCloudfrontS3 } from '../../envUtils';
import ArchieResponseHandler from '../ArchieWidgets/ArchieResponseHandler';
import { initITwinUtils} from './itwin_utils';
import './itwin_styles.css';
import { openDrawer } from 'store/reducers/menu';
import { dispatch } from 'store';
import { parseTwinupCookie, formatBearerToken, extractToken } from './auth_utils';
import { debugLog, startPerformanceTimer } from './logger';
import { waitForViewerReady, parseBoundingBox } from './cesium_utils';
import { fetchAndFormatArchieResult } from './archie_result_fetcher';
import { registerCesiumGlbHighlight, cleanupCesiumGlbHighlight } from './cesium_glb_highlight';
import { mountGaussianSplatPanel, unmountGaussianSplatPanel } from '../GaussianSplatPanel/GaussianSplatPanel';
import CompliancePanel from './CompliancePanel';
import './scene_api';                  // registers window.saveScene
import './scene_loader';               // registers window.loadScene
import './poi_manager';                // registers window.loadPOIsForScene
import './gaussian_splat_viewer';      // registers window.loadGaussianSplat / removeGaussianSplat / watchAndLoadGaussianSplat

// Configuration for POI loading behavior
window.POI_LOADING_MODE = process.env.REACT_APP_POI_MODE || 'on-demand';  // 'on-demand' | 'auto' (for rollback)
window.PROTECT_ORIGINAL_POIS_ON_SAVE = true;  // Prevent data loss during saves

// Safely parse the auth cookie. Returns {} when the cookie is missing or malformed
// (e.g. when running locally without an authenticated session).
initITwinUtils();
dispatch(openDrawer({ drawerOpen: true }));   // Open

window.TWINWORLD_DEBUG = window.TWINWORLD_DEBUG || true;
window.scenetype = 'default';

const sceneItems = (sceneType, selectedFolder, sceneList) => {
    const allScenes = sceneList.map(
        (scene, index) =>
            (selectedFolder === 'All' || (scene.folders && scene.folders.indexOf(selectedFolder) >= 0)) &&
            (sceneType === 'All Scenes' ||
                (scene.permission && scene.permission === 'draft' && sceneType === 'Drafts') ||
                (scene.permission && scene.permission === 'public' && sceneType === 'Completed')) && (
                <div
                    key={scene.scene_id}
                    style={{
                        display: 'grid',
                        placeItems: 'center',
                        width: '75px',
                        marginLeft: '5px',
                        marginRight: '5px',
                        cursor: 'pointer'
                    }}
                    className="item"
                    data-value="1"
                >
                    {!scene.local_glb_uri && (
                        <div
                            style={{
                                border: '2px solid rgb(243 111 75 / 56%)',
                                background: '#5c5a5a 50% / cover no-repeat',
                                borderRadius: '10px',
                                width: '75px',
                                height: '50px',
                                display: 'grid',
                                placeContent: 'center'
                            }}
                        >
                            <CircularProgress />
                        </div>
                    )}
                    {scene.local_glb_uri && (
                        <SceneTooltip
                            TransitionComponent={Zoom}
                            placement="bottom"
                            sx={{
                                width: '200px'
                            }}
                            title={
                                <React.Fragment>
                                    <Typography color="inherit" style={{ fontSize: '12px' }}>
                                        <b>Scene id</b> :{scene.scene_id}
                                    </Typography>
                                    <Typography color="inherit" style={{ fontSize: '12px' }}>
                                        <b>Name</b> :{scene.name ? scene.name : ' '}
                                    </Typography>
                                    <Typography color="inherit" style={{ fontSize: '12px' }}>
                                        <b>Coordinates</b>: ({scene.latitude}, {scene.longitude})
                                    </Typography>
                                    <Typography color="inherit" style={{ fontSize: '12px' }}>
                                        <b>Location</b>: {scene.placeName}
                                    </Typography>
                                </React.Fragment>
                            }
                            arrow
                        >
                            {!scene.name ? (
                                <model-viewer
                                    style={{
                                        border: '2px solid rgb(243 111 75 / 56%)',
                                        background: '#5c5a5a 50% / cover no-repeat',
                                        borderRadius: '10px',
                                        width: '75px',
                                        height: '50px'
                                    }}
                                    ondragstart={() => {
                                        drag(event);
                                    }}
                                    src={`${scene.local_glb_uri}`}
                                    poster={`${'https://dtuas2sql0dg4.cloudfront.net/build/hoverToLoad.png'}`}
                                    reveal="manual"
                                    data-value={JSON.stringify(scene)}
                                    onMouseOver={(ev) => {
                                        ev.target.poster = '';
                                        ev.target.dismissPoster();
                                    }}
                                    onMouseLeave={(ev) => {
                                        ev.target.poster = 'https://dtuas2sql0dg4.cloudfront.net/build/hoverToLoad.png';
                                    }}
                                    onClick={(ev) => {
                                        if (window.applyGlobeSpinEvent) {
                                            window.applyGlobeSpinEvent();
                                        }
                                        viewer.clock.onTick.removeEventListener(window.applyGlobeSpin);
                                        const dataValue = JSON.parse(ev.target.getAttribute('data-value'));
                                        window.sceneCreateOrUpdate = 'update';
                                        viewer.entities.removeAll();
                                        console.log(' 2 Calling window.loadScene with:', dataValue);
                                        window.loadScene(dataValue);
                                        window.sceneToUpdate = ev.target.getAttribute('data-value');
                                        window.addScene();
                                    }}
                                />
                            ) : (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    style={{
                                        border: '2px solid rgb(243 111 75 / 56%)',
                                        background: '#5c5a5a 50% / cover no-repeat',
                                        borderRadius: '10px',
                                        width: '75px',
                                        height: '50px',
                                        backgroundImage: `url('${scene.local_thumbnail_uri}')`
                                    }}
                                    ondragstart={() => {
                                        drag(event);
                                    }}
                                    data-value={JSON.stringify(scene)}
                                    onClick={(ev) => {
                                        if (window.applyGlobeSpinEvent) {
                                            window.applyGlobeSpinEvent();
                                        }
                                        viewer.clock.onTick.removeEventListener(window.applyGlobeSpin);
                                        const dataValue = JSON.parse(ev.target.getAttribute('data-value'));
                                        window.sceneCreateOrUpdate = 'update';
                                        console.log(' 3 Calling window.loadScene with:', dataValue);
                                        window.loadScene(dataValue);
                                        window.sceneToUpdate = ev.target.getAttribute('data-value');
                                        window.addScene();
                                    }}
                                    onKeyDown={(ev) => {
                                        if (window.applyGlobeSpinEvent) {
                                            window.applyGlobeSpinEvent();
                                        }
                                        viewer.clock.onTick.removeEventListener(window.applyGlobeSpin);
                                        const dataValue = JSON.parse(ev.target.getAttribute('data-value'));
                                        window.sceneCreateOrUpdate = 'update';
                                        viewer.entities.removeAll();
                                        console.log(' 4 Calling window.loadScene with:', dataValue);
                                        window.loadScene(dataValue);
                                        window.sceneToUpdate = ev.target.getAttribute('data-value');
                                        window.addScene();
                                    }}
                                />
                            )}
                        </SceneTooltip>
                    )}
                </div>
            )
    );
    return allScenes;
};

// window.saveScene is registered in ./scene_api.js (imported below)
const responsive = {
    0: { items: 1 },
    268: { items: 3, itemsFit: 'contain' },
    1024: { items: 3, itemsFit: 'contain' }
};

var BASE_GRAPHQL_URL = `${getBaseUrl()}/sim`;
// window.loadScene, loadBuildingScene, getEnvType → see ./scene_loader.js / scene_api.js
// iTwin scene functions (validateITwinConfig, hideITwinHeightSlider, etc.) → ./itwin_scene.js
// window.loadPOIsForScene is registered in ./poi_manager.js (imported below)

function Scenes() {
    const theme = useTheme();
    const [activeStep, setActiveStep] = React.useState(0);
    const [taskList, setTaskList] = React.useState([]);
    const [sceneList, setSceneList] = React.useState([]);
    const [selectedFolder, setSelectedFolder] = React.useState('All');
    const [sceneType, setSceneType] = React.useState('All Scenes');
    const [sceneItemsDom, setSceneItemsDom] = React.useState([]);
    const [receivedMessage, setReceivedMessage] = React.useState('');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [twinupCookie] = React.useState(Cookies.get(getCookie()));
    const [showLabel, setShowLabel] = React.useState(false);

    // Track event handlers and observers for cleanup
    const eventHandlersRef = React.useRef([]);
    const mutationObserverRef = React.useRef(null);
    const archieReactRootRef = React.useRef(null);

    const getSceneTwinData = (accessToken, sceneId) => {
        return axios
            .get(`${getBaseUrl()}/api/v1/twin/world/${sceneId}`, {
                headers: {
                    Authorization: formatBearerToken(accessToken)
                }
            })
            .then((response) => {
                return {
                    folders: response.data.folders,
                    permission: response.data.permission
                };
            });
    };

    const buildingDetails = (accessToken, buildingGuid, buildingS3Location, structuralSystemS3Location) => {
        console.log(`building ID: ${buildingGuid}`);

        const url = `${BASE_GRAPHQL_URL}`;
        const variables = {};
        const query = `
        query {
            Building(id: "${buildingGuid}") {
                id
                name
                s3location
                StructuralSystem{ 
                    s3location
                }
            }
        }`;

        if (structuralSystemS3Location || buildingS3Location) {
            return new Promise((resolve, reject) => {
                resolve({
                    data: {
                        Building: [
                            {
                                s3location: buildingS3Location,
                                StructuralSystem: {
                                    s3location: structuralSystemS3Location
                                }
                            }
                        ]
                    }
                });
            });
        } else {
            return axios
                .post(
                    url,
                    {
                        query,
                        variables
                    },
                    {
                        headers: {
                            Authorization: formatBearerToken(accessToken),
                            buildingid: extractToken(accessToken)
                        }
                    }
                )
                .then((response) => response.data);
        }
    };

    const getSceneTwin = (accessToken, sceneId) => {
        return new Promise((resolve, reject) => {
            axios
                .get(`${getBaseUrl()}/api/v1/twin/world/${sceneId}`, {
                    headers: {
                        Authorization: formatBearerToken(accessToken),
                        'Content-Type': 'application/json'
                    }
                })
                .then((response) => {
                    const twinRes = response.data;
                    const twinImages = twinRes.images;
                    if (twinImages && twinImages.length > 0) {
                        resolve({
                            image: twinImages[0].s3_url,
                            name: twinRes.name
                        });
                    } else {
                        resolve({
                            image: null,
                            name: null
                        });
                    }
                })
                .catch((err) => {
                    console.error('Failed to retrieve twin image');
                    resolve({
                        image: null,
                        name: null
                    });
                });
        });
    };

    const getBuildingUriGlb = (buildingId, buildingUriGlb, buildingUriThumbnail) => {
        // console.log(`callAPI with ${uri_glb}...`);
        const parseS3Url = require('parse-aws-s3-url');
        const parts = parseS3Url(buildingUriGlb);
        const thumbnailParts =
            buildingUriThumbnail && buildingUriThumbnail !== ''
                ? parseS3Url(buildingUriThumbnail)
                : {
                      Bucket: '',
                      Key: ''
                  };

        //=> parts = {Bucket: 'my-bucket', Key: 'path/to/my/file.png'}
        const bucket = parts.Bucket;
        const key = parts.Key;
        const thumbnailBucket = thumbnailParts.Bucket;
        const thumbnailKey = thumbnailParts.Key;

        return fetch(
            `${getBaseUrl()}/twinworld/getGlbModel?bucket=${bucket}&s3_key=${key}&thumbnail_bucket=${thumbnailBucket}&s3_thumbnail_key=${thumbnailKey}`
        )
            .then((res) => res.json())
            .then(
                (result) => {
                    // console.log(`Success: calling download_glb API with result ${JSON.stringify(result, null, 4)}`);
                    // console.log(`Result: ${JSON.stringify(result, null, 4)}`);
                    const glb_path = result.glb_path.replace('./utils', `${getBaseUrl()}/community/twinworld/utils`);
                    const thumbnail_path =
                        result.thumbnail_path && result.thumbnail_path !== ''
                            ? result.thumbnail_path.replace('./utils', `${getBaseUrl()}/community/twinworld/utils`)
                            : '';
                    // console.log(`GLB Path: ${JSON.stringify(glb_path)}`);
                    return {
                        glb: glb_path,
                        thumbnail: thumbnail_path
                    };
                },
                (error) => {
                    console.log(`Error: calling download_glb API with message ${error}`);
                }
            );
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    const setMessage = (message, resolveRes) => {
        // mutation.target.setAttribute('search_response', message);
        setReceivedMessage(message);
        resolveRes(message);
    };

    window.updateTotal = (category) => {
        const rows = document
            .querySelector('twinworld-app')
            .shadowRoot.querySelectorAll(`.expandable[data-category="${category}"] .unit-cost`);
        let categoryTotal = 0;
        rows.forEach((cell) => {
            const value = parseFloat(cell.textContent.replace(/[^0-9.-]+/g, ''));
            if (!isNaN(value)) {
                categoryTotal += value;
            }
        });
        const categoryTotalCell = document.querySelector('twinworld-app').shadowRoot.getElementById(`${category}-total-cost`);
        const groupCategoryTotal = document.querySelector('twinworld-app').shadowRoot.querySelector(`.${category}-total-amount`);
        categoryTotalCell.textContent = `$${categoryTotal.toLocaleString()}`;
        groupCategoryTotal.textContent = `$${categoryTotal.toLocaleString()}`;

        const allRows = document.querySelector('twinworld-app').shadowRoot.querySelectorAll(`.expandable[data-category] .unit-cost`);
        let finalTotal = 0;
        allRows.forEach((cell) => {
            const value = parseFloat(cell.textContent.replace(/[^0-9.-]+/g, ''));
            if (!isNaN(value)) {
                finalTotal += value;
            }
        });

        const finalTotalBox = document.querySelector('twinworld-app').shadowRoot.querySelector('.amount-total-value');
        finalTotalBox.textContent = finalTotal;
    };

    window.toggleVisibility = (category, element) => {
        //Hide all rows
        const allRows = document.querySelector('twinworld-app').shadowRoot.querySelectorAll(`.expandable[data-category]`);
        allRows.forEach((row) => {
            row.classList.remove('show');
        });

        const rows = document.querySelector('twinworld-app').shadowRoot.querySelectorAll(`.expandable[data-category="${category}"]`);
        rows.forEach((row) => {
            row.classList.add('show');
        });

        updateTotal(category);
    };

    window.updateNetCost = (inputElement, category) => {
        const row = inputElement.closest('tr');
        const rate = parseFloat(row.querySelector('.rate').value) || 0;
        const percent = row.querySelector('.percent');
        const quantity = parseFloat(row.querySelector('.quantity').value) || 1;
        const unitCostCell = row.querySelector('.unit-cost');

        // Calculate new unit cost
        const netCost = percent ? (rate * quantity) / 100 : rate * quantity;
        unitCostCell.textContent = `$${netCost.toLocaleString()}`;

        // Update total for the category
        updateTotal(category);
    };




    const renderArchieResponse = (responseData, isRegulation) => {
        debugLog('renderArchieResponse', 'Function called (React-based)', {
            hasCsvRepresentation: !!responseData.csv_representation,
            hasResponse: !!responseData.response,
            isRegulation,
            sheetTitle: responseData.sheetTitle,
            responseKeys: Object.keys(responseData)
        });

        try {
            // Get the shadow root and robot-scanner container
            const shadowRoot = document.querySelector('twinworld-app')?.shadowRoot;
            if (!shadowRoot) {
                console.error('twinworld-app shadow root not found');
                return 'Error: Shadow root not found';
            }
            
            const scannerContainer = shadowRoot.querySelector('#robot-scanner');
            if (!scannerContainer) {
                console.error('robot-scanner container not found');
                return 'Error: Container not found';
            }

            // Clear existing content
            scannerContainer.innerHTML = '';

            // ✅ ADD CLOSE BUTTON at top right
            const closeButton = document.createElement('button');
            closeButton.id = 'archie-panel-close-btn';
            closeButton.innerHTML = '✕';
            closeButton.style.cssText = `
                position: absolute;
                top: 12px;
                right: 12px;
                z-index: 10001;
                background: rgba(243, 111, 75, 0.9);
                color: white;
                border: none;
                border-radius: 50%;
                width: 32px;
                height: 32px;
                font-size: 18px;
                font-weight: bold;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s ease;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
            `;
            closeButton.onmouseover = () => {
                closeButton.style.background = 'rgba(243, 111, 75, 1)';
                closeButton.style.transform = 'scale(1.1)';
            };
            closeButton.onmouseout = () => {
                closeButton.style.background = 'rgba(243, 111, 75, 0.9)';
                closeButton.style.transform = 'scale(1)';
            };
            closeButton.onclick = () => {
                scannerContainer.style.display = 'none';
                scannerContainer.style.visibility = 'hidden';
                scannerContainer.style.opacity = 0;
                // Clear any active highlights
                if (window.scenetype === 'itwin') {
                    if (typeof window.restoreOriginalITwinColors === 'function') {
                        window.restoreOriginalITwinColors(window.itwinTileset);
                    }
                    if (typeof window.clearTooltipHandler === 'function') {
                        window.clearTooltipHandler();
                    }
                } else if (typeof window._clearCesiumGlbHighlight === 'function') {
                    window._clearCesiumGlbHighlight();
                }
            };
            scannerContainer.appendChild(closeButton);

            // ✅ ADD SCROLLABLE STYLES WITH ACCESSIBILITY
            scannerContainer.style.overflowY = 'auto';
            scannerContainer.style.overflowX = 'hidden';
            scannerContainer.style.maxHeight = '92vh';

            // ✅ RESET ANY TRANSFORMS THAT MIGHT INVERT SCROLL DIRECTION
            // Note: Applied both as inline styles and in injected stylesheet (!important)
            // to ensure maximum compatibility and override any external CSS rules
            scannerContainer.style.transform = 'none';
            scannerContainer.style.writingMode = 'horizontal-tb';
            scannerContainer.style.direction = 'ltr';

            // ✅ ENSURE NORMAL FLEX LAYOUT (not reversed)
            const computedStyle = window.getComputedStyle(scannerContainer);
            if (computedStyle.display === 'flex' || computedStyle.display === 'inline-flex') {
                const flexDir = computedStyle.flexDirection;
                if (flexDir === 'column-reverse' || flexDir === 'row-reverse') {
                    scannerContainer.style.flexDirection = flexDir.replace('-reverse', '');
                }
            }

            // ✅ MAKE SCROLLBAR ALWAYS VISIBLE
            scannerContainer.style.scrollbarWidth = 'thin'; // Firefox
            scannerContainer.style.scrollbarColor = '#888 #2a2a2a'; // Firefox: thumb track

            // WebKit scrollbar styling (Chrome, Safari, Edge)
            const styleId = 'robot-scanner-scrollbar-styles';
            if (!shadowRoot.querySelector(`#${styleId}`)) {
                const styleSheet = document.createElement('style');
                styleSheet.id = styleId;
                styleSheet.textContent = `
                    /* ✅ Push the panel's fixed container clear of the 64px left toolbar */
                    .robot-scanner-container {
                        left: 64px !important;
                        margin-left: 8px !important;
                        max-width: calc(100vw - 80px) !important;
                    }

                    #robot-scanner {
                        /* ✅ Force normal scroll direction */
                        transform: none !important;
                        writing-mode: horizontal-tb !important;
                        direction: ltr !important;
                    }
                    
                    #robot-scanner::-webkit-scrollbar {
                        width: 12px;
                    }
                    #robot-scanner::-webkit-scrollbar-track {
                        background: #2a2a2a;
                        border-radius: 6px;
                    }
                    #robot-scanner::-webkit-scrollbar-thumb {
                        background: #888;
                        border-radius: 6px;
                    }
                    #robot-scanner::-webkit-scrollbar-thumb:hover {
                        background: #555;
                    }
                `;
                shadowRoot.appendChild(styleSheet);
            }

            // Create a container div for React rendering
            const reactContainer = document.createElement('div');
            reactContainer.id = 'archie-response-container';
            scannerContainer.appendChild(reactContainer);

            // Cleanup previous React root if it exists
            if (archieReactRootRef.current) {
                try {
                    archieReactRootRef.current.unmount();
                } catch (err) {
                    console.warn('Error unmounting previous React root:', err);
                }
            }

            // Create callback for widget completion
            const handleComplete = (message) => {
                debugLog('renderArchieResponse', 'Widget rendering complete', { message });
                return message;
            };
            if (window.scenetype === 'itwin') {
                window.restoreOriginalITwinColors(window.itwinTileset);
                window.clearTooltipHandler();
            } else if (typeof window._clearCesiumGlbHighlight === 'function') {
                // Clear any previous GLB highlight before applying new result
                window._clearCesiumGlbHighlight();
            }

            // Create new React root and render the ArchieResponseHandler
            const root = createRoot(reactContainer);
            archieReactRootRef.current = root;
            if (responseData.tags.data_characteristics.includes('compliance')) {
                // ── Compliance Assessment flow ──────────────────────────────────────
                // Phase 1: render editable rules panel so user can review/adjust, then submit.
                // Phase 2 (inside CompliancePanel): re-poll and display PASS/FAIL results.
                const cookieData2 = parseTwinupCookie(twinupCookie);
                const complianceAccessToken = cookieData2.access_token;

                // Retrieve the original prompt_id stored when the job was submitted.
                // handleArchieRequest stores it on window so CompliancePanel can re-poll.
                const compliancePromptId = responseData._promptId || window._lastArchiePromptId || '';

                root.render(
                    <CompliancePanel
                        responseData={responseData}
                        accessToken={complianceAccessToken}
                        pollJobStatus={pollJobStatus}
                        promptId={compliancePromptId}
                    />
                );

                scannerContainer.style.display = 'block';
                scannerContainer.style.visibility = 'visible';
                scannerContainer.style.opacity = 0.8;
                return 'Compliance assessment loaded. Review rules and submit.';
            }
            else if (responseData.tags.data_characteristics.includes('text')){
                // Advice-mode results carry the text in `advice`; action results use `response`
                return responseData.advice || responseData.response || 'No response received.';
            }
            else if (responseData.tags.data_characteristics.includes('Fetch') && window.scenetype === 'itwin') {
                // iTwin Fetch: use iTwin-specific highlighting
                const itwin_entities = responseData.entities;
                const features = responseData.features;
                window.highlightMultipleITwinEntitiesWithTransparencyAndTooltip(window.itwinTileset, itwin_entities, '#ff00ff', features, 0.2, window.viewer);
                return "Building Highlighted Successfully";
            }
            else if (responseData.tags.data_characteristics.includes('Fetch') || 
                     responseData.tags.data_characteristics.includes('Asset Query')) {
                // Asset query: highlight Cesium GLB entities only (no left panel widget)
                // Early return - skip React rendering completely
                const entityIds = responseData.entityIds || [];
                const entityTooltipMap = responseData.entityTooltipMap || {};
                
                console.log(`🎯 [asset-query] Highlighting ${entityIds.length} Cesium GLB entities`);
                
                if (entityIds.length > 0 && window.scenetype !== 'itwin' && window.viewer) {
                    try {
                        window.highlightCesiumGlbEntities(entityIds, entityTooltipMap, responseData.tags);
                        return `${entityIds.length} building elements highlighted. Hover to see properties.`;
                    } catch (highlightErr) {
                        console.warn('Cesium GLB entity highlighting failed:', highlightErr.message);
                        return 'Failed to highlight entities on map';
                    }
                } else {
                    return 'No entities found to highlight (Cesium GLB scene required)';
                }
            }

            root.render(
                <ArchieResponseHandler
                    responseData={responseData}
                    isRegulation={isRegulation}
                    onComplete={handleComplete}
                />
            );

            // ── Highlight entity_ids in the Cesium GLB scene ────────────────
            // Only applies to normal (non-iTwin) scenes. The entity_ids are
            // comma-separated strings stored in layer/prim attributes and
            // extracted by archie_result_fetcher into entityIds + entityTooltipMap.
            const entityIds        = responseData.entityIds || [];
            const entityTooltipMap = responseData.entityTooltipMap || {};
            console.log('🎯 [Scenes.js] Entity highlight check →',
                'entityIds:', entityIds,
                '| scenetype:', window.scenetype,
                '| viewer:', !!window.viewer,
                '| highlightFn:', typeof window.highlightCesiumGlbEntities
            );
            if (entityIds.length > 0 && window.scenetype !== 'itwin' && window.viewer) {
                debugLog('renderArchieResponse', `Highlighting ${entityIds.length} GLB entities`, { entityIds });
                try {
                    window.highlightCesiumGlbEntities(entityIds, entityTooltipMap, responseData.tags);
                } catch (highlightErr) {
                    console.warn('Cesium GLB entity highlighting failed:', highlightErr.message);
                }
            }

            // Show the scanner container
            scannerContainer.style.display = 'block';
            scannerContainer.style.visibility = 'visible';
            scannerContainer.style.opacity = 0.8;

            debugLog('renderArchieResponse', 'React component rendered');

            // Return appropriate message based on response type
            if (responseData.descriptiveResponse) {
                return responseData.descriptiveResponse;
            } else if (responseData.csv_representation) {
                return 'Response is rendered in tabular format';
            } else {
                return 'Response rendered successfully';
            }

        } catch (error) {
            console.error('Error rendering Archie response:', error);
            debugLog('renderArchieResponse', 'Error occurred', { error: error.message });
            
            // Fallback to text display on error
            const scannerContainer = document.querySelector('twinworld-app').shadowRoot.querySelector('#robot-scanner');
            if (scannerContainer) {
                scannerContainer.innerHTML = `<div class="archie-widget-error">Error rendering response: ${error.message}</div>`;
                scannerContainer.style.display = 'block';
                scannerContainer.style.visibility = 'visible';
                scannerContainer.style.opacity = 0.8;
            }
            
            return `Error: ${error.message}`;
        }
    };

    // Legacy support: Keep the old renderArchieResponse logic as a fallback
    // This is commented out but available if needed for rollback

    // Helper function to validate and parse JSON response
    const parseJsonResponse = async (response, context = 'response') => {
        // Validate content-type before parsing
        const contentType = response.headers.get('content-type');
        
        // Read response text once
        const responseText = await response.text();
        
        if (!contentType || !contentType.includes('application/json')) {
            // Check if it's HTML error page
            if (responseText.trim().toLowerCase().startsWith('<!doctype') || 
                responseText.trim().toLowerCase().startsWith('<html')) {
                console.error(`❌ Server returned HTML instead of JSON in ${context}`);
                throw new Error('Server session expired. Please refresh the page and try again.');
            }
            
            throw new Error(`Expected JSON but got ${contentType || 'unknown type'}`);
        }

        // Parse JSON safely
        try {
            return JSON.parse(responseText);
        } catch (jsonError) {
            console.error(`❌ Invalid JSON in ${context}:`, responseText.substring(0, 200));
            throw new Error(`Invalid JSON in ${context}: ${jsonError.message}`);
        }
    };

    // Polling utility function for job status
    const pollJobStatus = async (pollUrl, options = {}) => {
        const {
            maxAttempts = 60,
            intervalMs = 2000,
            backoffMultiplier = 1.1,
            maxIntervalMs = 5000,
            onProgress = null,
            accessToken = null
        } = options;

        let currentInterval = intervalMs;
        
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                const response = await fetch(pollUrl, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(accessToken && { 'Authorization': formatBearerToken(accessToken) })
                    }
                });

                if (!response.ok) {
                    if (response.status === 404) {
                        console.warn('Job not found, will retry...');
                        await new Promise(resolve => setTimeout(resolve, currentInterval));
                        currentInterval = Math.min(currentInterval * backoffMultiplier, maxIntervalMs);
                        continue;
                    }
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                // Parse JSON response with validation
                const data = await parseJsonResponse(response, 'poll response');
                
                // Call progress callback if provided
                if (onProgress && data.progress !== undefined) {
                    onProgress(data.progress, data.message);
                }

                // Check job completion states
                if (data.status === 'completed' || data.status === 'done' || data.status === 'user_input_needed') {
                    return data.result;
                } else if (data.status === 'failed' || data.status === 'error') {
                    throw new Error(data.error || data.result?.error || 'Job failed');
                } else if (data.status === 'cancelled') {
                    throw new Error('Job was cancelled');
                }

                // Wait before next poll with exponential backoff
                await new Promise(resolve => setTimeout(resolve, currentInterval));
                currentInterval = Math.min(currentInterval * backoffMultiplier, maxIntervalMs);

            } catch (error) {
                if (attempt === maxAttempts - 1) {
                    // Avoid nested timeout messages
                    const errorMsg = error.message.includes('Polling timeout') 
                        ? error.message 
                        : `Polling timeout after ${maxAttempts} attempts: ${error.message}`;
                    throw new Error(errorMsg);
                }
                
                // For network errors, wait and retry
                if (error.message.includes('fetch') || error.message.includes('network')) {
                    console.warn(`Network error on attempt ${attempt + 1}, retrying...`);
                    await new Promise(resolve => setTimeout(resolve, currentInterval));
                    currentInterval = Math.min(currentInterval * backoffMultiplier, maxIntervalMs);
                    continue;
                }
                
                throw error;
            }
        }

        throw new Error('Polling timeout: Maximum attempts reached');
    };

    // Initialize progress UI in shadow DOM
    const initializeProgressUI = () => {
        const loaderBg = document.querySelector('twinworld-app').shadowRoot.querySelector('.loaderbg');
        if (!loaderBg) return;

        // Check if progress UI already exists
        let progressContainer = loaderBg.querySelector('#archie-progress-container');
        if (!progressContainer) {
            progressContainer = document.createElement('div');
            progressContainer.id = 'archie-progress-container';
            progressContainer.style.cssText = `
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 300px;
                background: rgba(0, 0, 0, 0.9);
                border-radius: 10px;
                padding: 20px;
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
                z-index: 10000;
            `;

            const progressBar = document.createElement('div');
            progressBar.id = 'archie-progress-bar';
            progressBar.style.cssText = `
                width: 100%;
                height: 8px;
                background: rgba(255, 255, 255, 0.2);
                border-radius: 4px;
                overflow: hidden;
                margin-bottom: 10px;
            `;

            const progressFill = document.createElement('div');
            progressFill.id = 'archie-progress-fill';
            progressFill.style.cssText = `
                width: 0%;
                height: 100%;
                background: linear-gradient(90deg, #4CAF50 0%, #8BC34A 100%);
                border-radius: 4px;
                transition: width 0.3s ease;
            `;

            const progressMessage = document.createElement('div');
            progressMessage.id = 'archie-progress-message';
            progressMessage.style.cssText = `
                color: white;
                font-size: 14px;
                text-align: center;
            `;
            progressMessage.textContent = 'Preparing your request...';

            progressBar.appendChild(progressFill);
            progressContainer.appendChild(progressBar);
            progressContainer.appendChild(progressMessage);
            loaderBg.appendChild(progressContainer);
        }

        return progressContainer;
    };

    // Update progress UI
    const updateProgressUI = (progress, customMessage) => {
        const progressContainer = document.querySelector('twinworld-app')?.shadowRoot?.querySelector('#archie-progress-container');
        if (!progressContainer) return;

        const progressFill = progressContainer.querySelector('#archie-progress-fill');
        const progressMessage = progressContainer.querySelector('#archie-progress-message');

        if (progressFill) {
            progressFill.style.width = `${progress}%`;
        }

        if (progressMessage) {
            let message = customMessage;
            if (!customMessage) {
                // Stage-based messages based on progress percentage
                if (progress < 30) {
                    message = 'Preparing your request...';
                } else if (progress < 70) {
                    message = 'Analyzing with AI...';
                } else if (progress < 95) {
                    message = 'Formatting results...';
                } else {
                    message = 'Almost done...';
                }
            }
            progressMessage.textContent = message;
        }
    };

    // Hide progress UI with fade animation
    const hideProgressUI = () => {
        const progressContainer = document.querySelector('twinworld-app')?.shadowRoot?.querySelector('#archie-progress-container');
        if (progressContainer) {
            progressContainer.style.transition = 'opacity 0.3s ease';
            progressContainer.style.opacity = '0';
            setTimeout(() => {
                progressContainer.remove();
            }, 300);
        }
    };

    // Handle Archie request with async job queue pattern
    const handleArchieRequest = async (text, followUpData = '', options = {}) => {
        const { mode = 'action' } = options;
        try {
            // Initialize progress UI
            initializeProgressUI();
            updateProgressUI(10, 'Submitting request...');

            // Extract access token from cookie (same pattern as line 2382)
            const cookieData = parseTwinupCookie(twinupCookie);
            const accessToken = cookieData.access_token;

            // Build arche-engine/submit request body
            const archieSubmitBody = {
                prompt:  `${text}`,
                mode:    mode,
                remote:  true,
                ...(window.sceneId && { sceneId: window.sceneId })
            };

            // Initiate job with POST to /arche-engine/submit
            const initiateResponse = await fetch(`${getBaseUrl()}/arche-engine/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': formatBearerToken(accessToken)
                },
                credentials: 'include',
                body: JSON.stringify(archieSubmitBody)
            });

            if (!initiateResponse.ok) {
                throw new Error(`Failed to initiate job: ${initiateResponse.status}`);
            }

            // Parse JSON response with validation - provide detailed error for HTML pages
            let initiateData;
            try {
                initiateData = await parseJsonResponse(initiateResponse, 'initiate response');
            } catch (error) {
                // Enhance error message for HTML error pages
                if (error.message.includes('session expired')) {
                    throw new Error('Server returned HTML error page. This usually means:\n' +
                                  '• Authentication failed or session expired\n' +
                                  '• Endpoint URL is incorrect\n' +
                                  '• Server encountered an internal error\n\n' +
                                  'Please refresh the page and try again.');
                }
                throw error;
            }
            
            // Response shape: { "message": "...", "id": "265e39cb-..." }
            // Poll at /arche-engine/status/{id}
            if (initiateData.id) {
                updateProgressUI(20, 'Request queued...');

                // Store prompt id globally so CompliancePanel can re-poll after user submits
                window._lastArchiePromptId = initiateData.id;

                const pollUrl = `${getBaseUrl()}/arche-engine/status/${initiateData.id}`;
                console.log('📊 Polling arche-engine status at:', pollUrl);

                // Poll for job completion — raw result is the arche-engine status result
                const rawResult = await pollJobStatus(pollUrl, {
                    maxAttempts: 60,
                    intervalMs: 2000,
                    backoffMultiplier: 1.1,
                    maxIntervalMs: 5000,
                    accessToken: accessToken,
                    onProgress: (progress, message) => {
                        updateProgressUI(progress, message);
                    }
                });

                updateProgressUI(90, 'Fetching scene data...');

                // Fetch and format scene data from TwinUp SceneDB (port of FetchResult_fr_SceneDB)
                const processedResult = await fetchAndFormatArchieResult(rawResult, accessToken, getBaseUrl, formatBearerToken);

                // Attach the prompt_id so CompliancePanel can re-poll after user submits
                if (processedResult && typeof processedResult === 'object') {
                    processedResult._promptId = initiateData.id;
                }

                updateProgressUI(100, 'Complete!');
                return processedResult;
            } else {
                // Synchronous response (backward compatibility)
                updateProgressUI(100, 'Complete!');
                return initiateData;
            }
        } catch (error) {
            console.error('❌ Error in handleArchieRequest:', error);
            
            // Transform technical errors into user-friendly messages
            let userMessage = error.message;
            
            if (error.message.includes('Failed to fetch')) {
                userMessage = 'Network error: Unable to connect to server. Please check your internet connection.';
            } else if (error.message.includes('Unexpected token')) {
                userMessage = 'Server returned invalid data. Please try again or contact support.';
            }
            
            throw new Error(userMessage);
        } finally {
            // Hide progress UI after a short delay
            setTimeout(() => {
                hideProgressUI();
            }, 500);
        }
    };

    // ============================================================================
    // SHARED UI HELPERS
    // ============================================================================

    /** Grab the twinworld-app shadow root */
    const getShadowRoot = () => document.querySelector('twinworld-app')?.shadowRoot;

    /** Hide the fullscreen loader overlay */
    const hideLoader = () => {
        const sr = getShadowRoot();
        if (sr) sr.querySelector('.loaderbg').style.display = 'none';
    };

    /** Get #promptInput — checks shadow root first, then document */
    const getPromptInput = () => {
        const sr = getShadowRoot();
        return (sr && sr.querySelector('#promptInput')) || document.querySelector('#promptInput');
    };

    /** Re-enable the prompt input after an optional delay */
    const enableInputDelayed = (ms = 1000) => {
        setTimeout(() => { const el = getPromptInput(); if (el) el.disabled = false; }, ms);
    };

    /** Returns context follow-up string when user is in a cost/zoning view */
    const getContextFollowUpData = () =>
        (window.activeOption?.startsWith('cost') || window.activeOption?.startsWith('zoning'))
            ? 'in the following data: ' + (window.contextData || '')
            : '';

    /**
     * Renders an Archie response, shows the CSV scanner panel when applicable,
     * and re-enables the prompt input.
     */
    const renderAndShowResult = (result) => {
        const response = renderArchieResponse(result);
        if (result?.csv_representation) {
            setTimeout(() => {
                const scanner = getShadowRoot()?.querySelector('#robot-scanner');
                if (scanner) {
                    scanner.style.display    = 'block';
                    scanner.style.visibility = 'visible';
                    scanner.style.opacity    = 0.8;
                }
            }, 2000);
        }
        enableInputDelayed();
        return response;
    };


    /** Handles: navigate_to_given_location */
    const handleNavigateIntent = async (openAIResponse) => {
        const { name: locationName } = openAIResponse;
        const { data } = await axios.get(
            `https://nominatim.openstreetmap.org/search?format=json&q=${locationName}`
        );
        const { boundingbox, lat, lon } = data[0];

        window.destBoundingBox = {
            south: (boundingbox[0] * Cesium.Math.PI) / 180,
            north: (boundingbox[1] * Cesium.Math.PI) / 180,
            west:  (boundingbox[2] * Cesium.Math.PI) / 180,
            east:  (boundingbox[3] * Cesium.Math.PI) / 180
        };

        setTimeout(() => {
            window.viewer?.camera?.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(lon, lat, 1005.0)
            });
        }, 2000);

        const sr = getShadowRoot();
        sr.querySelector('.simplebar-wrapper').parentNode.style.height = '100%';
        sr.querySelector('.simplebar-wrapper').parentNode.parentNode.parentNode.style.width = '64px';
        enableInputDelayed();
        return `Going to ${locationName} (${lat}, ${lon})`;
    };

    /**
     * Draws a balloon/map-pin emoji marker with a small numbered badge onto a canvas
     * and returns it as a data URL, for use as a Cesium billboard image. Cached per
     * index so repeated calls (re-renders) don't redraw the same canvas twice.
     */
    const _balloonPinCache = (handleNavigateIntent._balloonPinCache ??= {});
    const createBalloonPinDataUrl = (index) => {
        if (_balloonPinCache[index]) return _balloonPinCache[index];

        // Rendered at 2x scale internally for crisp edges, then displayed larger via
        // the billboard's width/height so pins stay clearly visible from a distance.
        const canvas = document.createElement('canvas');
        canvas.width = 96;
        canvas.height = 112;
        const ctx = canvas.getContext('2d');

        // Drop shadow beneath the pin for extra visibility/contrast against any basemap
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.55)';
        ctx.shadowBlur = 8;
        ctx.shadowOffsetY = 4;
        ctx.font = '84px serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText('📍', 48, 8);
        ctx.restore();

        // Numbered badge in the top-right corner
        ctx.beginPath();
        ctx.arc(70, 26, 20, 0, Math.PI * 2);
        ctx.fillStyle = '#1e293b';
        ctx.fill();
        ctx.lineWidth = 4;
        ctx.strokeStyle = '#fff';
        ctx.stroke();

        ctx.fillStyle = '#fff';
        ctx.font = 'bold 24px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(index), 70, 28);

        const dataUrl = canvas.toDataURL();
        _balloonPinCache[index] = dataUrl;
        return dataUrl;
    };

    /**
     * Registers (once per viewer) a MOUSE_MOVE screen-space handler that shows a
     * floating HTML tooltip with POI details when hovering over a POI pin billboard.
     * The tooltip element is injected directly into document.body (like the scene
     * billboard tooltip) so it renders above the shadow DOM regardless of z-index.
     */
    const registerPoiPinHoverTooltip = (activeViewer) => {
        if (!activeViewer || window._poiPinHoverRegisteredFor === activeViewer) return;
        window._poiPinHoverRegisteredFor = activeViewer;

        let tooltip = document.getElementById('poi-pin-tooltip');
        if (!tooltip) {
            tooltip = document.createElement('div');
            tooltip.id = 'poi-pin-tooltip';
            tooltip.style.cssText = [
                'position:fixed',
                'z-index:2147483646',
                'background:rgba(22,33,62,0.97)',
                'color:#fff',
                'padding:8px 14px',
                'border-radius:8px',
                'font-size:12px',
                'font-weight:500',
                'font-family:"Suisse Intl",sans-serif',
                'pointer-events:none',
                'display:none',
                'border:1px solid rgba(30,144,255,0.6)',
                'box-shadow:0 4px 14px rgba(0,0,0,0.55)',
                'max-width:240px',
                'line-height:1.5',
                'letter-spacing:0.2px'
            ].join(';');
            document.body.appendChild(tooltip);
        }

        const handler = new Cesium.ScreenSpaceEventHandler(activeViewer.scene.canvas);
        handler.setInputAction((movement) => {
            const picked = activeViewer.scene.pick(movement.endPosition);
            if (Cesium.defined(picked) && picked.id?.properties?.isPOIPin?.getValue?.()) {
                const props = picked.id.properties;
                const name = props.poiName?.getValue?.() ?? 'POI';
                const distance = props.poiDistanceMiles?.getValue?.();
                const lat = props.poiLatitude?.getValue?.();
                const lon = props.poiLongitude?.getValue?.();
                const idx = props.poiIndex?.getValue?.();

                tooltip.innerHTML = `
                    <div style="font-weight:700;color:#38bdf8;margin-bottom:3px">#${idx} ${name}</div>
                    ${distance !== undefined ? `<div>Distance: ${distance} mi</div>` : ''}
                    ${lat !== undefined ? `<div>Lat: ${Number(lat).toFixed(4)}, Lon: ${Number(lon).toFixed(4)}</div>` : ''}
                `;
                tooltip.style.display = 'block';

                const rect = activeViewer.scene.canvas.getBoundingClientRect();
                tooltip.style.left = `${rect.left + movement.endPosition.x + 14}px`;
                tooltip.style.top  = `${rect.top + movement.endPosition.y + 14}px`;
                activeViewer.scene.canvas.style.cursor = 'pointer';
            } else {
                tooltip.style.display = 'none';
                activeViewer.scene.canvas.style.cursor = '';
            }
        }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    };

    /**
     * Draws a distinct "project/home" pin (different color from POI pins) used to
     * mark the currently-loaded project's location when the camera zooms out to
     * show nearby POIs, so the user can click it to fly back in.
     */
    let _projectPinDataUrl = null;
    const createProjectPinDataUrl = () => {
        if (_projectPinDataUrl) return _projectPinDataUrl;

        const canvas = document.createElement('canvas');
        canvas.width = 96;
        canvas.height = 112;
        const ctx = canvas.getContext('2d');

        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.6)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetY = 4;
        ctx.font = '84px serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        // Use a house/building emoji so it's visually distinct from the 📍 POI pins
        ctx.fillText('🏢', 48, 8);
        ctx.restore();

        // Home badge (star) in the top-right corner to further distinguish it
        ctx.beginPath();
        ctx.arc(70, 26, 20, 0, Math.PI * 2);
        ctx.fillStyle = '#f36f4b';
        ctx.fill();
        ctx.lineWidth = 4;
        ctx.strokeStyle = '#fff';
        ctx.stroke();

        ctx.fillStyle = '#fff';
        ctx.font = 'bold 20px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('★', 70, 28);

        _projectPinDataUrl = canvas.toDataURL();
        return _projectPinDataUrl;
    };

    /**
     * Registers (once per viewer) a LEFT_CLICK handler that flies the camera back
     * to the view it had right before a location-intel search zoomed out, whenever
     * the user clicks the project pin dropped by handleLocationIntelIntent.
     */
    const registerProjectPinClickHandler = (activeViewer) => {
        if (!activeViewer || window._projectPinClickRegisteredFor === activeViewer) return;
        window._projectPinClickRegisteredFor = activeViewer;

        const handler = new Cesium.ScreenSpaceEventHandler(activeViewer.scene.canvas);
        handler.setInputAction((clickEvent) => {
            const picked = activeViewer.scene.pick(clickEvent.position);
            if (Cesium.defined(picked) && picked.id?.properties?.isProjectPin?.getValue?.()) {
                const pinEntity = picked.id;
                const savedView = window._preLocationIntelCameraView;

                // Remove the project pin itself once clicked — it's a one-time
                // "fly back to building" affordance and shouldn't linger after use.
                const removeProjectPin = () => {
                    if (activeViewer.entities.contains(pinEntity)) {
                        activeViewer.entities.remove(pinEntity);
                    }
                    if (Array.isArray(window.poiFlagPoints)) {
                        window.poiFlagPoints = window.poiFlagPoints.filter((e) => e !== pinEntity);
                    }
                };

                if (savedView) {
                    activeViewer.camera.flyTo({
                        destination: savedView.destination,
                        orientation: {
                            heading: savedView.heading,
                            pitch: savedView.pitch,
                            roll: savedView.roll
                        },
                        duration: 1.5,
                        complete: removeProjectPin,
                        cancel: removeProjectPin
                    });
                } else if (window.currentProjectScene?.latitude && window.currentProjectScene?.longitude) {
                    // Fallback: fly to a reasonable overhead view of the project location
                    activeViewer.camera.flyTo({
                        destination: Cesium.Cartesian3.fromDegrees(
                            window.currentProjectScene.longitude,
                            window.currentProjectScene.latitude,
                            500
                        ),
                        duration: 1.5,
                        complete: removeProjectPin,
                        cancel: removeProjectPin
                    });
                } else {
                    removeProjectPin();
                }
            }
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    };

    /**
     * Handles: query_location_and_find_neighborhood_buildings_within_given_bounding_area
     * Submits the raw prompt straight to Arche Engine — the LocationIntelAgent
     * (backed by LocationIntelTool) parses the poi_type + radius directly from the
     * prompt text (e.g. "10 mile radius") and queries OpenStreetMap for nearby POIs.
     * The returned POIs are then dropped as pins on the map.
     */
    const handleLocationIntelIntent = async (text) => {
        const sr = getShadowRoot();
        sr.querySelector('.loaderbg').style.display = 'block';
        document.querySelector('#promptInput').disabled = true;
        sr.querySelector('#robot-scanner').style.display = 'none';

        try {
            // 1. Submit the prompt directly to Arche Engine (LocationIntelAgent).
            //    NOTE: we deliberately do NOT use handleArchieRequest()/fetchAndFormatArchieResult()
            //    here — that formatter requires scene_id/stage_ids/layer_ids_map on the result
            //    (it's built for scene-entity fetch/highlight flows) and would throw for a
            //    location-intel result, which instead carries a flat { poi_type, pois, ... } payload.
            //    So we submit + poll directly and use the raw job result as-is.
            initializeProgressUI();
            updateProgressUI(10, 'Submitting location intelligence request...');

            const cookieData = parseTwinupCookie(twinupCookie);
            const accessToken = cookieData.access_token;

            const archieSubmitBody = {
                prompt: text,
                mode: 'action',
                remote: true,
                ...(window.sceneId && { sceneId: window.sceneId })
            };

            const initiateResponse = await fetch(`${getBaseUrl()}/arche-engine/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': formatBearerToken(accessToken)
                },
                credentials: 'include',
                body: JSON.stringify(archieSubmitBody)
            });

            if (!initiateResponse.ok) {
                throw new Error(`Failed to initiate location intel job: ${initiateResponse.status}`);
            }

            const initiateData = await parseJsonResponse(initiateResponse, 'location intel initiate response');
            window._lastArchiePromptId = initiateData.id;

            const pollUrl = `${getBaseUrl()}/arche-engine/status/${initiateData.id}`;
            const rawJobResult = await pollJobStatus(pollUrl, {
                maxAttempts: 60,
                intervalMs: 2000,
                backoffMultiplier: 1.1,
                maxIntervalMs: 5000,
                accessToken,
                onProgress: (progress, message) => updateProgressUI(progress, message)
            });

            updateProgressUI(100, 'Complete!');

            // Result shape (from LocationIntelAgent/LocationIntelTool):
            // { result: { poi_type, radius_miles, radius_meters, center, count, pois: [...] }, tags: {...} }
            const rawResult = typeof rawJobResult === 'string' ? JSON.parse(rawJobResult) : rawJobResult;
            const locationResult = rawResult?.result ?? rawResult;
            const pois = locationResult?.pois || [];
            const poiType = locationResult?.poi_type || 'place';
            const radiusMiles = locationResult?.radius_miles;

            // Guard: if Arche Engine didn't return POI data, this wasn't really a
            // location-intel-shaped result — fall back to the generic Archie flow.
            if (!Array.isArray(pois) || pois.length === 0) {
                console.warn('handleLocationIntelIntent: no POIs returned — falling back to Archie intent. Got:', locationResult);
                hideLoader();
                enableInputDelayed();
                if (locationResult && typeof locationResult === 'object' && locationResult.count === 0) {
                    return `No ${poiType}s found within ${radiusMiles ?? ''} mile radius.`;
                }
                return handleArchieIntent(text);
            }

            // 2. Clear existing POI flags
            window.scanState  = 'scanning';
            window.entityList = [];
            window.poiFlagPoints?.forEach(p => window.viewer?.entities.remove(p));
            window.poiFlagPoints = [];

            window.locationIntelSearchData = {
                buildingType: poiType,
                requestedCoverageMeters: locationResult?.radius_meters,
                sceneId: window.newSceneId
            };

            window.allPickedAssets           = pois;
            window.changeOriginOnClickGlobal = false;
            window.scanState                 = 'complete';

            // 3. Build entity list for context panel
            window.entityList = pois.map((p) => (
                <span>
                    {p.name}<br />
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Distance: {p.distance_miles} mi<br />
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Lat: {Number(p.latitude).toFixed(3)}&nbsp; Lon: {Number(p.longitude).toFixed(3)}
                </span>
            ));

            // 4. Drop POI pins on the map — a single balloon/pin-emoji billboard per POI,
            //    clamped to ground (height fixed at 0) so it doesn't visually drift while
            //    zooming/panning, plus a hover tooltip showing the POI's details.
            const activeViewer = window.viewer || viewer;

            pois.forEach((poi, index) => {
                const pos = Cesium.Cartesian3.fromDegrees(poi.longitude, poi.latitude, 0);

                const poiEntity = activeViewer.entities.add({
                    name: poi.name || 'POI',
                    position: pos,
                    billboard: {
                        image: createBalloonPinDataUrl(index + 1),
                        width: 56,
                        height: 65,
                        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                        disableDepthTestDistance: Number.POSITIVE_INFINITY,
                        scaleByDistance: undefined,
                        eyeOffset: new Cesium.Cartesian3(0, 0, 0)
                    },
                    properties: {
                        isPOIPin: true,
                        poiName: poi.name,
                        poiDistanceMiles: poi.distance_miles,
                        poiLatitude: poi.latitude,
                        poiLongitude: poi.longitude,
                        poiIndex: index + 1
                    }
                });
                window.poiFlagPoints.push(poiEntity);
            });

            // Register (once) the hover handler that shows POI details in a floating tooltip.
            registerPoiPinHoverTooltip(activeViewer);

            // 4b. Drop a distinct "project" pin at the currently-loaded project's location
            //     (if known) so the user can click it to fly the camera back to the project
            //     after the view has zoomed out to show the POIs.
            if (window.currentProjectScene?.latitude && window.currentProjectScene?.longitude) {
                // Save the current camera view *before* we fly out to the POIs, so clicking
                // the project pin can restore exactly this view ("as earlier").
                window._preLocationIntelCameraView = {
                    destination: Cesium.Cartesian3.clone(activeViewer.camera.position),
                    heading: activeViewer.camera.heading,
                    pitch: activeViewer.camera.pitch,
                    roll: activeViewer.camera.roll
                };

                const projectPos = Cesium.Cartesian3.fromDegrees(
                    window.currentProjectScene.longitude,
                    window.currentProjectScene.latitude,
                    0
                );
                const projectPinEntity = activeViewer.entities.add({
                    name: window.currentProjectScene.name || 'Project',
                    position: projectPos,
                    billboard: {
                        image: createProjectPinDataUrl(),
                        width: 60,
                        height: 70,
                        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                        disableDepthTestDistance: Number.POSITIVE_INFINITY
                    },
                    label: {
                        text: window.currentProjectScene.name || 'Project',
                        font: 'bold 14px "Suisse Intl", sans-serif',
                        fillColor: Cesium.Color.WHITE,
                        showBackground: true,
                        backgroundColor: Cesium.Color.fromCssColorString('#f36f4b').withAlpha(0.9),
                        backgroundPadding: new Cesium.Cartesian2(8, 5),
                        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                        pixelOffset: new Cesium.Cartesian2(0, -78),
                        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                        disableDepthTestDistance: Number.POSITIVE_INFINITY
                    },
                    properties: { isProjectPin: true }
                });
                window.poiFlagPoints.push(projectPinEntity);

                registerProjectPinClickHandler(activeViewer);
            }

            // 5. Refresh context panel
            const contextBtn = sr.getElementById('contextData');
            contextBtn?.click();
            setTimeout(() => contextBtn?.click(), 20);

            // 6. Fly/zoom the camera so the newly-dropped POI pins are actually
            //    visible — previously the camera only nudged its heading by a
            //    tiny amount, so pins far from the current view were never seen.
            try {
                if (window.poiFlagPoints.length > 0 && activeViewer) {
                    await activeViewer.flyTo(window.poiFlagPoints, {
                        duration: 1.5,
                        offset: new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-90), 0)
                    });
                }
            } catch (flyErr) {
                console.warn('Failed to fly camera to POI pins, falling back to zoomTo:', flyErr.message);
                try {
                    activeViewer?.zoomTo(window.poiFlagPoints);
                } catch (zoomErr) {
                    console.warn('zoomTo fallback also failed:', zoomErr.message);
                }
            }

            document.querySelectorAll('.chatAgent > div > div > div > div')
                    .forEach(el => (el.style.whiteSpace = 'pre-line'));
            hideLoader();
            enableInputDelayed();

            const assetList = pois
                .map((p, i) => `(${i + 1}) ${p.name} — ${p.distance_miles} mi (Lat: ${Number(p.latitude).toFixed(3)}, Lon: ${Number(p.longitude).toFixed(3)})`)
                .join('\n\n');

            return `Found ${pois.length} ${poiType}${pois.length !== 1 ? 's' : ''} within ${radiusMiles ?? ''} mile radius:\n\n${assetList}`;

        } catch (error) {
            console.error('❌ Error in handleLocationIntelIntent:', error);
            hideLoader();
            enableInputDelayed();
            return `Failed to fetch location intelligence: ${error.message}`;
        } finally {
            setTimeout(() => hideProgressUI(), 500);
        }
    };

    /**
     * Draws a traffic-incident pin (⚠️ triangle) colored by severity so incidents
     * are visually distinct from POI/project pins. Cached per severity level.
     */
    const _trafficPinCache = {};
    const createTrafficPinDataUrl = (severityColor) => {
        if (_trafficPinCache[severityColor]) return _trafficPinCache[severityColor];

        const canvas = document.createElement('canvas');
        canvas.width = 88;
        canvas.height = 88;
        const ctx = canvas.getContext('2d');

        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.6)';
        ctx.shadowBlur = 8;
        ctx.shadowOffsetY = 3;
        // Colored circle backdrop
        ctx.beginPath();
        ctx.arc(44, 40, 32, 0, Math.PI * 2);
        ctx.fillStyle = severityColor;
        ctx.fill();
        ctx.lineWidth = 4;
        ctx.strokeStyle = '#fff';
        ctx.stroke();
        ctx.restore();

        ctx.font = '36px serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('⚠️', 44, 40);

        const dataUrl = canvas.toDataURL();
        _trafficPinCache[severityColor] = dataUrl;
        return dataUrl;
    };

    const TRAFFIC_SEVERITY_COLORS = {
        0: '#64748b', // unknown
        1: '#facc15', // minor
        2: '#fb923c', // moderate
        3: '#ef4444', // major
        4: '#991b1b'  // undefined/severe
    };

    // Same TomTom key used by TrafficIntelTool on the backend — needed here too
    // since the flow-tile textures are requested directly by the browser.
    const TOMTOM_API_KEY = 'cuH7HY5JfZqDQD4xHCbeQYCAtuLCaVKq';

    // Track all ground primitives currently rendering the traffic flow heatmap,
    // so they can be cleanly removed later.
    window._trafficFlowPrimitives = window._trafficFlowPrimitives || [];

    /** Converts an XYZ slippy-map tile coordinate to its west/east/north/south lon/lat bounds. */
    const _tileToRectangleDegrees = (x, y, z) => {
        const n = Math.pow(2, z);
        const west = (x / n) * 360 - 180;
        const east = ((x + 1) / n) * 360 - 180;
        const latRad = (yy) => {
            const a = Math.PI - (2 * Math.PI * yy) / n;
            return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(a) - Math.exp(-a)));
        };
        const north = latRad(y);
        const south = latRad(y + 1);
        return { west, east, north, south };
    };

    /** Picks a slippy-map zoom level that keeps the tile count reasonable for a given radius. */
    const _zoomForRadiusMiles = (radiusMiles) => {
        const r = radiusMiles || 2;
        if (r <= 1) return 17;
        if (r <= 3) return 16;
        if (r <= 8) return 15;
        if (r <= 15) return 14;
        return 12;
    };

    /**
     * Loads a tile image (via an Image element so we can await it / keep the network
     * request cache-friendly). NOTE: we intentionally do NOT manually flip the image —
     * Cesium already flips the Y-axis internally when uploading Image/Canvas sources to
     * a WebGL texture, so a manual pre-flip here would double-flip and make the overlay
     * render even more incorrectly positioned than doing nothing at all.
     */
    const _loadTileImage = (url) => new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });

    /**
     * Overlays TomTom's live Traffic Flow raster tiles as a road-congestion heatmap
     * (green = free-flowing, red = heavily congested) — same visual style as Google/
     * Apple Maps traffic layers.
     *
     * IMPORTANT: We deliberately do NOT use a Cesium.ImageryLayer here. Imagery layers
     * only drape onto the classic Globe/terrain surface — they are invisible whenever a
     * Cesium3DTileset (e.g. Google Photorealistic 3D Tiles) sits geometrically on top of
     * the globe, since the opaque tileset mesh completely occludes the draped imagery.
     * Instead we render one Cesium.GroundPrimitive per required tile, with
     * classificationType = CESIUM_3D_TILE (+ terrain), which Cesium supports draping
     * directly onto 3D tileset surfaces as well as classic terrain.
     */
    window.showTrafficFlowLayer = async (activeViewerArg, center, radiusMiles) => {
        const v = activeViewerArg || window.viewer;
        if (!v) return;

        // Clear any previously-added heatmap primitives before adding new ones.
        window.hideTrafficFlowLayer(v);

        const lat = center?.latitude ?? window.currentProjectScene?.latitude;
        const lon = center?.longitude ?? window.currentProjectScene?.longitude;
        if (typeof lat !== 'number' || typeof lon !== 'number') return;

        const zoom = _zoomForRadiusMiles(radiusMiles);
        const n = Math.pow(2, zoom);
        const centerX = Math.floor(((lon + 180) / 360) * n);
        const latRad = (lat * Math.PI) / 180;
        const centerY = Math.floor(
            ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
        );

        // Cover roughly the requested radius with a square grid of tiles (capped to keep
        // the primitive count/network requests reasonable).
        const milesPerTileAtZoom = (24901 * Math.cos(latRad)) / n; // Mercator-adjusted approximation
        const tileSpan = Math.min(4, Math.max(1, Math.ceil((radiusMiles || 2) / Math.max(milesPerTileAtZoom, 0.1))));

        const tileCoords = [];
        for (let dx = -tileSpan; dx <= tileSpan; dx++) {
            for (let dy = -tileSpan; dy <= tileSpan; dy++) {
                const x = centerX + dx;
                const y = centerY + dy;
                if (y < 0 || y >= n) continue;
                tileCoords.push({ x, y });
            }
        }

        // A newer call may have superseded this one while tiles were loading — guard
        // against a stale async response adding primitives after a subsequent clear.
        const requestToken = (window._trafficFlowRequestToken = (window._trafficFlowRequestToken || 0) + 1);

        await Promise.all(tileCoords.map(async ({ x, y }) => {
            const url = `https://api.tomtom.com/traffic/map/4/tile/flow/relative0/${zoom}/${x}/${y}.png?key=${TOMTOM_API_KEY}`;
            try {
                const tileImage = await _loadTileImage(url);
                if (window._trafficFlowRequestToken !== requestToken) return; // superseded

                const { west, east, north, south } = _tileToRectangleDegrees(x, y, zoom);
                const rectangle = Cesium.Rectangle.fromDegrees(west, south, east, north);

                const primitive = new Cesium.GroundPrimitive({
                    geometryInstances: new Cesium.GeometryInstance({
                        geometry: new Cesium.RectangleGeometry({
                            rectangle,
                            vertexFormat: Cesium.MaterialAppearance.MaterialSupport.TEXTURED.vertexFormat
                        })
                    }),
                    appearance: new Cesium.MaterialAppearance({
                        material: Cesium.Material.fromType('Image', { image: tileImage }),
                        translucent: true,
                        materialSupport: Cesium.MaterialAppearance.MaterialSupport.TEXTURED
                    }),
                    classificationType: Cesium.ClassificationType.BOTH
                });
                v.scene.primitives.add(primitive);
                window._trafficFlowPrimitives.push(primitive);
            } catch (tileErr) {
                console.warn(`Failed to add traffic flow tile ${zoom}/${x}/${y}:`, tileErr.message || tileErr);
            }
        }));
    };

    /** Removes all traffic-flow heatmap ground primitives, if present. */
    window.hideTrafficFlowLayer = (activeViewerArg) => {
        const v = activeViewerArg || window.viewer;
        // Bump the request token so any in-flight tile loads from a superseded
        // showTrafficFlowLayer() call are discarded instead of adding stale primitives.
        window._trafficFlowRequestToken = (window._trafficFlowRequestToken || 0) + 1;
        if (!v || !Array.isArray(window._trafficFlowPrimitives)) return;
        window._trafficFlowPrimitives.forEach((p) => {
            try {
                if (v.scene.primitives.contains(p)) {
                    v.scene.primitives.remove(p);
                }
            } catch (e) { /* no-op */ }
        });
        window._trafficFlowPrimitives = [];
    };

    /* ORIGINAL imagery-layer implementation (kept for reference / fallback, unused):
    window.showTrafficFlowLayer = (activeViewerArg) => {
        const v = activeViewerArg || window.viewer;
        if (!v) return;

        // If we think we already have a layer, verify it's actually still attached
        // to THIS viewer's imagery layer collection (it may be stale after a
        // remount/hot-reload where cleanup didn't run against the current viewer).
        if (window._trafficFlowLayer && v.imageryLayers.contains(window._trafficFlowLayer)) {
            window._trafficFlowLayer.show = true;
            window._trafficFlowLayer.alpha = 1.0;
            return;
        }
        window._trafficFlowLayer = null;

        const provider = new Cesium.UrlTemplateImageryProvider({
            url: `https://api.tomtom.com/traffic/map/4/tile/flow/relative0/{z}/{x}/{y}.png?key=${TOMTOM_API_KEY}`,
            credit: 'Traffic flow © TomTom',
            maximumLevel: 22
        });
        const layer = v.imageryLayers.addImageryProvider(provider);
        layer.show = true;
        layer.alpha = 1.0;
        window._trafficFlowLayer = layer;
    };
    */

    /**
     * Handles ad-hoc traffic queries (e.g. "show me the traffic near the project",
     * "traffic within 2 miles"). Submits the prompt directly to Arche Engine's
     * TrafficIntelAgent (backed by TrafficIntelTool / TomTom Traffic API), then
     * drops color-coded incident pins on the map plus a congestion summary.
     */
    const handleTrafficIntelIntent = async (text) => {
        const sr = getShadowRoot();
        sr.querySelector('.loaderbg').style.display = 'block';
        document.querySelector('#promptInput').disabled = true;
        sr.querySelector('#robot-scanner').style.display = 'none';

        try {
            initializeProgressUI();
            updateProgressUI(10, 'Submitting traffic intelligence request...');

            const cookieData = parseTwinupCookie(twinupCookie);
            const accessToken = cookieData.access_token;

            const archieSubmitBody = {
                prompt: text,
                mode: 'action',
                remote: true,
                ...(window.sceneId && { sceneId: window.sceneId })
            };

            const initiateResponse = await fetch(`${getBaseUrl()}/arche-engine/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': formatBearerToken(accessToken)
                },
                credentials: 'include',
                body: JSON.stringify(archieSubmitBody)
            });

            if (!initiateResponse.ok) {
                throw new Error(`Failed to initiate traffic intel job: ${initiateResponse.status}`);
            }

            const initiateData = await parseJsonResponse(initiateResponse, 'traffic intel initiate response');
            window._lastArchiePromptId = initiateData.id;

            const pollUrl = `${getBaseUrl()}/arche-engine/status/${initiateData.id}`;
            const rawJobResult = await pollJobStatus(pollUrl, {
                maxAttempts: 60,
                intervalMs: 2000,
                backoffMultiplier: 1.1,
                maxIntervalMs: 5000,
                accessToken,
                onProgress: (progress, message) => updateProgressUI(progress, message)
            });

            updateProgressUI(100, 'Complete!');

            // Result shape (from TrafficIntelAgent/TrafficIntelTool):
            // { result: { radius_miles, center, incident_count, incidents: [...], flow: {...} } }
            const rawResult = typeof rawJobResult === 'string' ? JSON.parse(rawJobResult) : rawJobResult;
            const trafficResult = rawResult?.result ?? rawResult;
            const incidents = trafficResult?.incidents || [];
            const flow = trafficResult?.flow;
            const radiusMiles = trafficResult?.radius_miles;

            if (rawResult?.error) {
                hideLoader();
                enableInputDelayed();
                return `Failed to fetch traffic data: ${rawResult.error}`;
            }

            // Clear existing POI/traffic pins
            window.scanState  = 'scanning';
            window.poiFlagPoints?.forEach(p => window.viewer?.entities.remove(p));
            window.poiFlagPoints = [];
            window.scanState = 'complete';

            const activeViewer = window.viewer || viewer;

            incidents.forEach((incident) => {
                const pos = Cesium.Cartesian3.fromDegrees(incident.longitude, incident.latitude, 0);
                const color = TRAFFIC_SEVERITY_COLORS[incident.magnitude_of_delay] || TRAFFIC_SEVERITY_COLORS[0];

                const incidentEntity = activeViewer.entities.add({
                    name: incident.category || 'Traffic Incident',
                    position: pos,
                    billboard: {
                        image: createTrafficPinDataUrl(color),
                        width: 48,
                        height: 48,
                        verticalOrigin: Cesium.VerticalOrigin.CENTER,
                        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                        disableDepthTestDistance: Number.POSITIVE_INFINITY
                    },
                    properties: {
                        isPOIPin: true,
                        poiName: `${incident.category}: ${incident.description || ''}`.trim(),
                        poiDistanceMiles: incident.distance_miles,
                        poiLatitude: incident.latitude,
                        poiLongitude: incident.longitude,
                        poiIndex: '⚠'
                    }
                });
                window.poiFlagPoints.push(incidentEntity);
            });

            registerPoiPinHoverTooltip(activeViewer);

            // Drop a "project" pin at the currently-loaded building's location (same as
            // handleLocationIntelIntent) so the user has a clear way to fly back to the
            // building after the camera zooms out to show traffic incidents.
            if (window.currentProjectScene?.latitude && window.currentProjectScene?.longitude) {
                window._preLocationIntelCameraView = {
                    destination: Cesium.Cartesian3.clone(activeViewer.camera.position),
                    heading: activeViewer.camera.heading,
                    pitch: activeViewer.camera.pitch,
                    roll: activeViewer.camera.roll
                };

                const projectPos = Cesium.Cartesian3.fromDegrees(
                    window.currentProjectScene.longitude,
                    window.currentProjectScene.latitude,
                    0
                );
                const projectPinEntity = activeViewer.entities.add({
                    name: window.currentProjectScene.name || 'Project',
                    position: projectPos,
                    billboard: {
                        image: createProjectPinDataUrl(),
                        width: 60,
                        height: 70,
                        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                        disableDepthTestDistance: Number.POSITIVE_INFINITY
                    },
                    label: {
                        text: window.currentProjectScene.name || 'Project',
                        font: 'bold 14px "Suisse Intl", sans-serif',
                        fillColor: Cesium.Color.WHITE,
                        showBackground: true,
                        backgroundColor: Cesium.Color.fromCssColorString('#f36f4b').withAlpha(0.9),
                        backgroundPadding: new Cesium.Cartesian2(8, 5),
                        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                        pixelOffset: new Cesium.Cartesian2(0, -78),
                        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                        disableDepthTestDistance: Number.POSITIVE_INFINITY
                    },
                    properties: { isProjectPin: true }
                });
                window.poiFlagPoints.push(projectPinEntity);

                registerProjectPinClickHandler(activeViewer);
            }

            // Overlay TomTom's road congestion heatmap (green/yellow/red flow tiles) alongside the incident pins.
            // Centered on the query location (falls back to the project location) with a tile
            // spread sized to the requested search radius. Not awaited — tiles load in the
            // background while the camera flies to the incident pins.
            try {
                const heatmapCenter = trafficResult?.center || {
                    latitude: window.currentProjectScene?.latitude,
                    longitude: window.currentProjectScene?.longitude
                };
                Promise.resolve(window.showTrafficFlowLayer(activeViewer, heatmapCenter, radiusMiles))
                    .catch((flowLayerErr) => console.warn('Failed to show traffic flow heatmap layer:', flowLayerErr?.message || flowLayerErr));
            } catch (flowLayerErr) {
                console.warn('Failed to show traffic flow heatmap layer:', flowLayerErr.message);
            }

            if (window.poiFlagPoints.length > 0) {
                try {
                    await activeViewer.flyTo(window.poiFlagPoints, {
                        duration: 1.5,
                        offset: new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-90), 0)
                    });
                } catch (flyErr) {
                    console.warn('Failed to fly camera to traffic incidents:', flyErr.message);
                }
            }

            hideLoader();
            enableInputDelayed();

            const flowSummary = flow
                ? `Current traffic flow: ${flow.congestion_level} (${flow.congestion_percent ?? '—'}% congestion, ` +
                  `${flow.current_speed_mph ?? '—'} mph vs ${flow.free_flow_speed_mph ?? '—'} mph free-flow).`
                : '';

            const incidentList = incidents
                .map((inc, i) => `(${i + 1}) ${inc.category}: ${inc.description || 'No description'} — ${inc.distance_miles} mi away`)
                .join('\n\n');

            if (incidents.length === 0) {
                return `No active traffic incidents found within ${radiusMiles ?? ''} mile radius.\n\n${flowSummary}`;
            }

            return `Found ${incidents.length} traffic incident${incidents.length !== 1 ? 's' : ''} within ${radiusMiles ?? ''} mile radius:\n\n${incidentList}\n\n${flowSummary}`;

        } catch (error) {
            console.error('❌ Error in handleTrafficIntelIntent:', error);
            hideLoader();
            enableInputDelayed();
            return `Failed to fetch traffic intelligence: ${error.message}`;
        } finally {
            setTimeout(() => hideProgressUI(), 500);
        }
    };

    /**
     * Handles Archie-based intents:
     *   - tell_me_details_of_building_or_scene
     *   - refers_to_past_tense_or_something_that_is_higher_or_above  (chainRequests: true)
     *   - refers_to_generic_calculation_or_information
     *   - query_about_location  (promptTransform to append geo context)
     */
    const handleArchieIntent = async (text, { chainRequests = false, promptTransform = null, mode = 'action' } = {}) => {
        const promptText   = promptTransform ? promptTransform(text) : text;
        const followUpData = getContextFollowUpData();
        const result       = await handleArchieRequest(promptText, followUpData, { chainRequests, mode });
        return renderAndShowResult(result);
    };

    // ============================================================================
    // INTENT ROUTER
    // ============================================================================

    /**
     * Entry point for all user prompts.
     * Detects intent via the OpenAI agent and delegates to the appropriate handler.
     */
    const handleSubmit = async (text, mode = 'action') => {
        const endTimer = startPerformanceTimer('handleSubmit');
        debugLog('handleSubmit', 'Starting prompt submission', {
            promptText: text,
            mode,
            SceneId: window.sceneId,
            pathname: window.location.pathname
        });

        try {
            const searchSceneId = window.location.pathname.indexOf('twinworld') >= 0
                ? window.location.pathname.split('/').splice(-1)[0]
                : undefined;

            // Create the analyze-menu UI element the first time a prompt arrives on non-scene pages
            if (!searchSceneId && !document.querySelector('.createAnalyzeMenu')) {
                const muiStack = document.querySelector('.MuiStack-root');
                if (muiStack) {
                    const el = document.createElement('div');
                    el.classList.add('createAnalyzeMenu');
                    muiStack.parentNode.append(el);
                    const sr = getShadowRoot();
                    const geocoderInput = sr?.querySelector('.cesium-geocoder-input');
                    const searchBtn     = sr?.querySelector('[aria-label="search"]');
                    const mailbox       = sr?.querySelector('[aria-label="mailbox folders"]');
                    const searchBtnBtn  = sr?.querySelector('.searchBtn button');
                    if (geocoderInput) geocoderInput.style.transform = 'translateY(30px)';
                    if (searchBtn)     searchBtn.style.transform     = 'translateX(30px) translateY(-10px)';
                    if (mailbox)       mailbox.style.background      = 'black';
                    if (searchBtnBtn)  searchBtnBtn.style.transform  = 'translateX(300px) translateY(-10px)';
                }
            }

            getShadowRoot().querySelector('.loaderbg').style.display = 'block';
            if (document.querySelector('#promptInput')) document.querySelector('#promptInput').disabled = true;

            // Clear any leftover POI/traffic/project pins and the traffic-flow heatmap from a
            // previous location-intel or traffic-intel query. Those handlers only clear their
            // own pins at the START of their own run — so if the user's next prompt routes to
            // a different intent entirely (e.g. a normal Archie query), the old pins/heatmap
            // would otherwise linger on the map forever. handleLocationIntelIntent /
            // handleTrafficIntelIntent immediately re-populate these themselves if that's
            // the intent that gets routed to below, so clearing here unconditionally is safe.
            if (Array.isArray(window.poiFlagPoints) && window.poiFlagPoints.length > 0) {
                window.poiFlagPoints.forEach(p => window.viewer?.entities.remove(p));
                window.poiFlagPoints = [];
            }
            if (typeof window.hideTrafficFlowLayer === 'function') {
                window.hideTrafficFlowLayer();
            }

            let result;

            // Fast-path: traffic queries are handled locally (the OpenAI intent
            // classifier doesn't have a dedicated intent for this yet), so detect
            // them via keyword matching before hitting the classifier.
            if (/\btraffic\b|\bcongestion\b|\broad\s*closure\b|\baccidents?\s+(near|around)\b/i.test(text)) {
                debugLog('Flow', 'Traffic keyword match — routing to TrafficIntelAgent', { text });
                result = await handleTrafficIntelIntent(text);
                endTimer();
                return result;
            }

            // Detect intent via OpenAI agent
            const { data: openAIResponse } = await axios.post(
                `${getBaseUrl()}/twinworld/usdaJsonAgent`,
                { userPrompt: text, name: '', interpretOnly: true },
                { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
            );

            debugLog('API Response', 'Intent detected', { intent: openAIResponse.intent });
            getShadowRoot().querySelector('.loaderbg').style.display = 'none';

            switch (openAIResponse.intent) {
                case 'navigate_to_given_location':
                    result = await handleNavigateIntent(openAIResponse);
                    break;

                case 'query_location_and_find_neighborhood_buildings_within_given_bounding_area':
                    result = await handleLocationIntelIntent(text);
                    break;

                case 'tell_me_details_of_building_or_scene':
                case 'refers_to_generic_calculation_or_information':
                    result = await handleArchieIntent(text, { mode });
                    break;

                case 'refers_to_past_tense_or_something_that_is_higher_or_above':
                    result = await handleArchieIntent(text, { chainRequests: true, mode });
                    break;

                case 'query_about_location':
                    result = await handleArchieIntent(text, {
                        mode,
                        promptTransform: (t) =>
                            `${t} : Find answer to this question based on the latitude and longitude information in the given data.`
                    });
                    break;                 

                default:
                    // Unknown or unrecognised intent — route to Archie which handles
                    // compliance, regulations, and any other domain-specific prompts.
                    debugLog('Flow', 'Unknown intent — falling back to Archie', { intent: openAIResponse.intent });
                    result = await handleArchieIntent(text, { mode });
                    break;
            }

            endTimer();
            return result;

        } catch (error) {
            console.error('Unexpected error in handleSubmit:', error);
            debugLog('Error', 'Unexpected error in handleSubmit', { error: error.message, stack: error.stack });
            try {
                hideLoader();
                if (document.querySelector('#promptInput')) document.querySelector('#promptInput').disabled = false;
            } catch (uiError) {
                console.error('Error cleaning up UI:', uiError);
            }
            endTimer();
            return 'An unexpected error occurred. Please try again.';
        } finally {
            setSearchTerm(text);
        }
    };

    // Setup and cleanup window functions
    React.useEffect(() => {
        // Initialize POI globals safely
        if (typeof window.allPickedAssets === 'undefined') {
            window.allPickedAssets = [];
        }
        if (typeof window.entityList === 'undefined') {
            window.entityList = [];
        }
        if (typeof window.poiFlagPoints === 'undefined') {
            window.poiFlagPoints = [];
        }
        
        window._poisLoadedForScene = false;
        
        console.log('🔧 POI loading mode:', window.POI_LOADING_MODE);
        
        // Store references to maintain backward compatibility
        window.renderArchieResponse = renderArchieResponse;

        // Mount the Gaussian Splat panel into the shadow DOM
        // (deferred slightly to ensure shadow DOM is fully ready)
        setTimeout(() => mountGaussianSplatPanel(), 2000);

        // Register the Cesium GLB entity highlighting helper (see cesium_glb_highlight.js)
        registerCesiumGlbHighlight();

        // Expose model loading banner setter for scene_loader.js
        // Uses direct DOM injection into document.body so it always renders on top,
        // regardless of shadow DOM / CSS transform contexts.
        const BANNER_ID = 'model-loading-banner';
        let _bannerDismissTimer = null;

        window.setModelLoadingBanner = (status) => {
            if (_bannerDismissTimer) { clearTimeout(_bannerDismissTimer); _bannerDismissTimer = null; }

            let banner = document.getElementById(BANNER_ID);
            if (!banner) {
                banner = document.createElement('div');
                banner.id = BANNER_ID;
                banner.style.cssText = [
                    'position:fixed',
                    'top:16px',
                    'left:50%',
                    'transform:translateX(-50%)',
                    'z-index:2147483647',
                    'padding:10px 28px',
                    'border-radius:12px',
                    'font-family:"Suisse Intl",sans-serif',
                    'font-size:14px',
                    'font-weight:600',
                    'letter-spacing:0.5px',
                    'color:#fff',
                    'display:flex',
                    'align-items:center',
                    'gap:10px',
                    'box-shadow:0 4px 20px rgba(0,0,0,0.55)',
                    'transition:background 0.3s ease,border-color 0.3s ease',
                    'pointer-events:none',
                ].join(';');
                document.body.appendChild(banner);
            }

            if (status === 'loading') {
                banner.style.background = 'linear-gradient(90deg,#1a1a2e 0%,#16213e 100%)';
                banner.style.border = '1px solid rgba(243,111,75,0.6)';
                banner.innerHTML = `
                    <svg width="16" height="16" viewBox="0 0 16 16" style="animation:tw-spin 0.9s linear infinite;flex-shrink:0">
                        <circle cx="8" cy="8" r="6" fill="none" stroke="rgba(243,111,75,0.35)" stroke-width="2"/>
                        <path d="M8 2a6 6 0 0 1 6 6" fill="none" stroke="rgb(243,111,75)" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                    <span>Loading Model</span>
                `;
                // Inject keyframe if not present
                if (!document.getElementById('tw-banner-style')) {
                    const s = document.createElement('style');
                    s.id = 'tw-banner-style';
                    s.textContent = '@keyframes tw-spin{to{transform:rotate(360deg)}}';
                    document.head.appendChild(s);
                }
            } else if (status === 'loaded') {
                banner.style.background = 'linear-gradient(90deg,#1b4332 0%,#2d6a4f 100%)';
                banner.style.border = '1px solid rgba(74,222,128,0.5)';
                banner.innerHTML = `<span style="font-size:15px">✅</span><span>Model Loaded</span>`;
                _bannerDismissTimer = setTimeout(() => {
                    if (banner.parentNode) banner.parentNode.removeChild(banner);
                    _bannerDismissTimer = null;
                }, 3000);
            }
        };

        // ✅ WEATHER + AIR QUALITY WIDGET (top-right, 40px down, refreshes every 2 minutes)
        // Rendered directly into document.body so it always sits above the shadow DOM.
        const WEATHER_WIDGET_ID = 'weather-air-quality-widget';
        const OPENWEATHER_API_KEY = '811aec9d921ed18fcb7555a663efa13e';
        let _weatherRefreshInterval = null;
        let _weatherCurrentLatLon = null;
        let _weatherCollapsed = false;
        let _lastWeatherData = null;
        let _lastAqiData = null;

        const AQI_LABELS = {
            1: { text: 'Good', color: '#22c55e', bg: 'rgba(34,197,94,0.18)' },
            2: { text: 'Fair', color: '#a3e635', bg: 'rgba(163,230,53,0.18)' },
            3: { text: 'Moderate', color: '#facc15', bg: 'rgba(250,204,21,0.18)' },
            4: { text: 'Poor', color: '#fb923c', bg: 'rgba(251,146,60,0.18)' },
            5: { text: 'Very Poor', color: '#f87171', bg: 'rgba(248,113,113,0.2)' }
        };

        // Injects the keyframes + scrollbar-free styling used by the widget (once)
        const ensureWeatherWidgetStyles = () => {
            if (document.getElementById('weather-widget-style')) return;
            const s = document.createElement('style');
            s.id = 'weather-widget-style';
            s.textContent = `
                @keyframes weather-widget-fade-in {
                    from { opacity: 0; transform: translateY(-8px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes weather-widget-shimmer {
                    0%   { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
                #${WEATHER_WIDGET_ID} { animation: weather-widget-fade-in 0.4s ease-out; transition: min-width 0.25s ease, padding 0.25s ease; }
                #${WEATHER_WIDGET_ID} .ww-pill {
                    display:flex; flex-direction:column; align-items:center; justify-content:center;
                    border-radius:10px; padding:6px 4px; flex:1; min-width:0;
                }
                #${WEATHER_WIDGET_ID} .ww-pill .ww-pill-value { font-size:13px; font-weight:700; }
                #${WEATHER_WIDGET_ID} .ww-pill .ww-pill-label { font-size:9px; opacity:0.75; letter-spacing:0.3px; margin-top:1px; }
                #${WEATHER_WIDGET_ID} .ww-toggle-btn {
                    pointer-events:auto; cursor:pointer; background:rgba(255,255,255,0.1);
                    border:1px solid rgba(255,255,255,0.2); border-radius:8px; color:#fff;
                    width:22px; height:22px; display:flex; align-items:center; justify-content:center;
                    font-size:12px; line-height:1; flex-shrink:0; transition:background 0.2s ease;
                }
                #${WEATHER_WIDGET_ID} .ww-toggle-btn:hover { background:rgba(255,255,255,0.22); }
            `;
            document.head.appendChild(s);
        };

        const ensureWeatherWidget = () => {
            ensureWeatherWidgetStyles();
            let widget = document.getElementById(WEATHER_WIDGET_ID);
            if (!widget) {
                widget = document.createElement('div');
                widget.id = WEATHER_WIDGET_ID;
                widget.style.cssText = [
                    'position:fixed',
                    'top:40px',
                    'right:16px',
                    'z-index:2147483647',
                    'padding:14px 18px',
                    'border-radius:16px',
                    'font-family:"Suisse Intl",sans-serif',
                    'font-size:13px',
                    'color:#fff',
                    'background:linear-gradient(135deg,#2b1055 0%,#1a1a2e 50%,#0f3460 100%)',
                    'border:1px solid rgba(255,255,255,0.12)',
                    'box-shadow:0 8px 28px rgba(0,0,0,0.55), 0 0 0 1px rgba(243,111,75,0.15) inset',
                    'display:flex',
                    'flex-direction:column',
                    'gap:8px',
                    'min-width:210px',
                    'backdrop-filter:blur(6px)',
                    'pointer-events:none'
                ].join(';');
                document.body.appendChild(widget);
            }
            return widget;
        };

        // Toggles the widget between expanded (full details) and collapsed
        // (compact single-line summary) so it doesn't overlap the chatbox as it expands.
        window.__toggleWeatherWidgetCollapse = () => {
            _weatherCollapsed = !_weatherCollapsed;
            renderWeatherWidgetContent(_lastWeatherData, _lastAqiData);
        };

        const renderWeatherWidgetContent = (weatherData, aqiData) => {
            _lastWeatherData = weatherData;
            _lastAqiData = aqiData;

            const widget = ensureWeatherWidget();
            const tempC = weatherData?.main?.temp !== undefined ? (weatherData.main.temp - 273.15).toFixed(1) : null;
            const feelsLikeC = weatherData?.main?.feels_like !== undefined ? (weatherData.main.feels_like - 273.15).toFixed(1) : null;
            const humidity = weatherData?.main?.humidity;
            const description = weatherData?.weather?.[0]?.description;
            const icon = weatherData?.weather?.[0]?.icon;
            const windSpeed = weatherData?.wind?.speed;
            const cityName = weatherData?.name;

            const components = aqiData?.list?.[0]?.components || {};
            const aqiValue = aqiData?.list?.[0]?.main?.aqi;
            const aqiInfo = AQI_LABELS[aqiValue] || { text: '—', color: '#ccc', bg: 'rgba(255,255,255,0.08)' };

            const fmt = (v, digits = 1) => (v !== undefined && v !== null ? v.toFixed(digits) : '—');

            const toggleButton = `
                <button class="ww-toggle-btn" onclick="window.__toggleWeatherWidgetCollapse()" title="${_weatherCollapsed ? 'Expand' : 'Collapse'}">
                    ${_weatherCollapsed ? '▾' : '▴'}
                </button>
            `;

            if (_weatherCollapsed) {
                widget.style.minWidth = 'unset';
                widget.style.padding = '8px 12px';
                widget.innerHTML = `
                    <div style="display:flex;align-items:center;gap:10px;">
                        ${icon ? `<img src="https://openweathermap.org/img/wn/${icon}.png" width="26" height="26" style="flex-shrink:0"/>` : '🌤️'}
                        <span style="font-weight:700;font-size:14px">${tempC !== null ? `${tempC}°C` : '—'}</span>
                        <span style="background:${aqiInfo.bg};color:${aqiInfo.color};padding:1px 8px;border-radius:20px;font-size:10px;font-weight:700;border:1px solid ${aqiInfo.color}55">
                            ${aqiInfo.text}
                        </span>
                        ${toggleButton}
                    </div>
                `;
                return;
            }

            widget.style.minWidth = '210px';
            widget.style.padding = '14px 18px';
            widget.innerHTML = `
                <div style="display:flex;align-items:center;justify-content:space-between">
                    <div style="display:flex;align-items:center;gap:8px;">
                        ${icon ? `<img src="https://openweathermap.org/img/wn/${icon}@2x.png" width="42" height="42" style="flex-shrink:0;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4))"/>` : '🌤️'}
                        <div>
                            <div style="font-weight:800;font-size:20px;line-height:1.1;background:linear-gradient(90deg,#ffd166,#f8961e,#f3722c);-webkit-background-clip:text;background-clip:text;color:transparent">
                                ${tempC !== null ? `${tempC}°C` : '—'}
                            </div>
                            <div style="font-size:11px;color:#e2e8f0;text-transform:capitalize">${description || 'Weather unavailable'}</div>
                        </div>
                    </div>
                    <div style="display:flex;align-items:center;gap:8px;">
                        ${cityName ? `<div style="font-size:10px;color:#94a3b8;max-width:70px;text-align:right;font-weight:600">📍 ${cityName}</div>` : ''}
                        ${toggleButton}
                    </div>
                </div>

                <div style="display:flex;gap:6px">
                    <div class="ww-pill" style="background:rgba(56,189,248,0.15)">
                        <span class="ww-pill-value" style="color:#38bdf8">${feelsLikeC !== null ? `${feelsLikeC}°` : '—'}</span>
                        <span class="ww-pill-label">FEELS LIKE</span>
                    </div>
                    <div class="ww-pill" style="background:rgba(96,165,250,0.15)">
                        <span class="ww-pill-value" style="color:#60a5fa">${humidity !== undefined ? `${humidity}%` : '—'}</span>
                        <span class="ww-pill-label">HUMIDITY</span>
                    </div>
                    <div class="ww-pill" style="background:rgba(167,139,250,0.15)">
                        <span class="ww-pill-value" style="color:#a78bfa">${windSpeed !== undefined ? `${windSpeed}` : '—'}</span>
                        <span class="ww-pill-label">WIND m/s</span>
                    </div>
                </div>

                <div style="height:1px;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.25),transparent)"></div>

                <div>
                    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">
                        <span style="font-size:12px;font-weight:700;color:#e2e8f0">🌬️ Air Quality</span>
                        <span style="background:${aqiInfo.bg};color:${aqiInfo.color};padding:2px 10px;border-radius:20px;font-size:11px;font-weight:700;border:1px solid ${aqiInfo.color}55">
                            ${aqiInfo.text}
                        </span>
                    </div>
                    <div style="display:flex;gap:6px">
                        <div class="ww-pill" style="background:rgba(248,113,113,0.15)">
                            <span class="ww-pill-value" style="color:#f87171">${fmt(components.pm2_5)}</span>
                            <span class="ww-pill-label">PM2.5</span>
                        </div>
                        <div class="ww-pill" style="background:rgba(251,146,60,0.15)">
                            <span class="ww-pill-value" style="color:#fb923c">${fmt(components.pm10)}</span>
                            <span class="ww-pill-label">PM10</span>
                        </div>
                        <div class="ww-pill" style="background:rgba(250,204,21,0.15)">
                            <span class="ww-pill-value" style="color:#facc15">${fmt(components.o3)}</span>
                            <span class="ww-pill-label">O₃</span>
                        </div>
                        <div class="ww-pill" style="background:rgba(163,230,53,0.15)">
                            <span class="ww-pill-value" style="color:#a3e635">${fmt(components.no2)}</span>
                            <span class="ww-pill-label">NO₂</span>
                        </div>
                    </div>
                    <div style="display:flex;gap:6px;margin-top:6px">
                        <div class="ww-pill" style="background:rgba(52,211,153,0.15)">
                            <span class="ww-pill-value" style="color:#34d399">${fmt(components.so2)}</span>
                            <span class="ww-pill-label">SO₂</span>
                        </div>
                        <div class="ww-pill" style="background:rgba(232,121,249,0.15)">
                            <span class="ww-pill-value" style="color:#e879f9">${fmt(components.co)}</span>
                            <span class="ww-pill-label">CO</span>
                        </div>
                        <div class="ww-pill" style="background:rgba(148,163,184,0.15)">
                            <span class="ww-pill-value" style="color:#94a3b8">${fmt(components.nh3)}</span>
                            <span class="ww-pill-label">NH₃</span>
                        </div>
                        <div class="ww-pill" style="background:rgba(148,163,184,0.15)">
                            <span class="ww-pill-value" style="color:#94a3b8">${fmt(components.no)}</span>
                            <span class="ww-pill-label">NO</span>
                        </div>
                    </div>
                    <div style="font-size:9px;color:#64748b;margin-top:6px;text-align:right">µg/m³</div>
                </div>
            `;
        };

        const fetchWeatherAndAirQuality = (lat, lon) => {
            const weatherUrl = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${OPENWEATHER_API_KEY}`;
            const aqiUrl = `https://api.openweathermap.org/data/2.5/air_pollution?lat=${lat}&lon=${lon}&appid=${OPENWEATHER_API_KEY}`;

            Promise.all([
                fetch(weatherUrl).then((res) => (res.ok ? res.json() : null)).catch(() => null),
                fetch(aqiUrl).then((res) => (res.ok ? res.json() : null)).catch(() => null)
            ])
                .then(([weatherData, aqiData]) => {
                    renderWeatherWidgetContent(weatherData, aqiData);
                })
                .catch((err) => {
                    console.warn('Failed to fetch weather/air quality:', err.message);
                });
        };

        // Starts (or restarts) the widget for a given lat/lon, refreshing every 2 minutes
        window.startWeatherAirQualityWidget = (latitude, longitude) => {
            if (typeof latitude !== 'number' || typeof longitude !== 'number' || isNaN(latitude) || isNaN(longitude)) {
                console.warn('startWeatherAirQualityWidget: invalid coordinates', latitude, longitude);
                return;
            }
            _weatherCurrentLatLon = { latitude, longitude };
            ensureWeatherWidget();
            fetchWeatherAndAirQuality(latitude, longitude);

            if (_weatherRefreshInterval) clearInterval(_weatherRefreshInterval);
            _weatherRefreshInterval = setInterval(() => {
                if (_weatherCurrentLatLon) {
                    fetchWeatherAndAirQuality(_weatherCurrentLatLon.latitude, _weatherCurrentLatLon.longitude);
                }
            }, 2 * 60 * 1000); // every 2 minutes
        };

        window.stopWeatherAirQualityWidget = () => {
            if (_weatherRefreshInterval) {
                clearInterval(_weatherRefreshInterval);
                _weatherRefreshInterval = null;
            }
            const widget = document.getElementById(WEATHER_WIDGET_ID);
            if (widget && widget.parentNode) widget.parentNode.removeChild(widget);
            _weatherCurrentLatLon = null;
        };

        // ✅ AUTO-HIDE "Ask MY Arch-e..." SUBMENU WHEN USER FOCUSES/TYPES
        const INPUT_RETRY_DELAY_MS = 500; // Delay before retrying to find input element
        const MAX_INPUT_RETRY_ATTEMPTS = 10; // Maximum number of retries to prevent infinite loop
        let retryCount = 0;
        
        const setupInputListeners = () => {
            // #promptInput lives inside the twinworld-app shadow root
            const sr = document.querySelector('twinworld-app')?.shadowRoot;
            const promptInput = sr
                ? sr.querySelector('#promptInput')
                : document.querySelector('#promptInput');

            if (!promptInput) {
                // Retry after a delay if input not found yet, up to max attempts
                if (retryCount < MAX_INPUT_RETRY_ATTEMPTS) {
                    retryCount++;
                    setTimeout(setupInputListeners, INPUT_RETRY_DELAY_MS);
                } else {
                    console.warn('Unable to find #promptInput after', MAX_INPUT_RETRY_ATTEMPTS, 'attempts');
                }
                return null;
            }

            const handleInputInteraction = () => {
                const searchSubmenu = document.querySelector('twinworld-app')?.shadowRoot?.querySelector('#search-submenu');
                if (searchSubmenu && searchSubmenu.style.display === 'inline-table') {
                    searchSubmenu.style.display = 'none';
                    // Restore original placeholder
                    const chatInput = document.querySelector('.chatAgent input');
                    if (chatInput) {
                        chatInput.placeholder = 'Ask MY Arch-e...';
                    }
                }
            };

            promptInput.addEventListener('focus', handleInputInteraction);
            promptInput.addEventListener('input', handleInputInteraction);

            // Return cleanup function
            return () => {
                promptInput.removeEventListener('focus', handleInputInteraction);
                promptInput.removeEventListener('input', handleInputInteraction);
            };
        };

        const cleanupInputListeners = setupInputListeners();

        // Cleanup on unmount
        return () => {
            if (typeof cleanupInputListeners === 'function') {
                cleanupInputListeners();
            }
            delete window.renderArchieResponse;
            delete window.setModelLoadingBanner;
            cleanupCesiumGlbHighlight();
            const b = document.getElementById('model-loading-banner');
            if (b) b.parentNode.removeChild(b);
            if (typeof window.stopWeatherAirQualityWidget === 'function') window.stopWeatherAirQualityWidget();
            delete window.startWeatherAirQualityWidget;
            delete window.stopWeatherAirQualityWidget;
            if (typeof window.hideTrafficFlowLayer === 'function') window.hideTrafficFlowLayer();
            delete window.showTrafficFlowLayer;
            delete window.hideTrafficFlowLayer;
            delete window.saveScene;
            delete window.loadScene;
            // Remove all active Gaussian Splats and unmount the panel
            if (typeof window.removeGaussianSplat === 'function') window.removeGaussianSplat();
            unmountGaussianSplatPanel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {

        // const twinupCookie = Cookies.get(getCookie());
        const cookieData = parseTwinupCookie(twinupCookie);
        // document.querySelector('twinworld-app').shadowRoot.querySelector('.blackbg').style.display = 'none';
        document.querySelector('twinworld-app').shadowRoot.querySelector('.loaderbg').style.display = 'block';

        const sceneUrl = new URLSearchParams(window.location.search);
        const searchSceneId =
            window.location.pathname.indexOf('twinworld') >= 0 ? window.location.pathname.split('/').splice(-1)[0] : undefined;

        console.log('🚀 PHASE 1: Loading All Scenes');
        console.log('⏱️  Start Time:', new Date().toISOString());
        console.log('📍 Search Scene ID:', searchSceneId);
        console.log('🍪 Cookie Data:', cookieData);

        if (searchSceneId) {
            if (document.querySelector('#folderWrapper')) {
                document.querySelector('#folderWrapper').style.display = 'none';
            }
            document.querySelector('twinworld-app').shadowRoot.querySelector('#cesiumContainer div').remove();
            if (document.querySelector('twinworld-app').shadowRoot.querySelector('#root header')) {
                document.querySelector('twinworld-app').shadowRoot.querySelector('#root header').remove();
            }
            if (document.querySelector('twinworld-app').shadowRoot.querySelector('#twoDimThreeDimSwitch')) {
                document.querySelector('twinworld-app').shadowRoot.querySelector('#twoDimThreeDimSwitch').remove();
            }
            if (
                document.querySelector('twinworld-app').shadowRoot.querySelector('.simplebar-wrapper') &&
                document.querySelector('twinworld-app').shadowRoot.querySelector('.simplebar-wrapper').parentNode.parentNode.parentNode
                    .parentNode
            ) {
                document
                    .querySelector('twinworld-app')
                    .shadowRoot.querySelector('.simplebar-wrapper').parentNode.parentNode.parentNode.parentNode.style.display = 'none';
            }
            if (document.querySelector('.createAnalyzeMenu')) {
                document.querySelector('.createAnalyzeMenu').remove();
            }
        }
        if (!document.querySelector('#papaparse')) {
            var script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.3.0/papaparse.min.js';
            script.id = 'papaparse';
            document.head.appendChild(script);
        }
        // Wait for viewer before proceeding with scene operations
        (async () => {
            // ── Step 1: Wait for Cesium viewer ──────────────────────────────────────
            const viewer = await waitForViewerReady();
            console.log('✅ Viewer initialized, proceeding with scene loading');
            viewer.scene.mode = Cesium.SceneMode.SCENE2D;

            // ── Step 2: Fetch scenes from API ────────────────────────────────────────
            // On localhost, cross-origin cookies are blocked by the browser (CORS/SameSite),
            // so we also send the token explicitly in the Authorization header.
            const _cookieData = parseTwinupCookie(twinupCookie);
            const _accessToken = _cookieData.access_token;
            const _authHeaders = _accessToken
                ? { 'Authorization': formatBearerToken(_accessToken) }
                : {};

            const scenesRes = await fetch(
                searchSceneId
                    ? `${getBaseUrl()}/api/v2/scene/${searchSceneId}?layer_depth=0&omit_layer_attributes=false`
                    : `${getBaseUrl()}/api/v2/scene/list?user_owned_only=true&omit_scene_attributes=false`,
                { credentials: 'include', headers: { 'accept': 'application/json', ..._authHeaders } }
            );
            console.log('✅ Scenes API response received, status:', scenesRes.status);
            if (!scenesRes.ok) {
                const text = await scenesRes.text();
                throw new Error(`Scenes API returned ${scenesRes.status}: ${text.substring(0, 200)}`);
            }
            let allScenes = await scenesRes.json();

            // Single-scene deep-link returns one object, not an array – normalise to array
            if (searchSceneId && !Array.isArray(allScenes)) {
                allScenes = allScenes && typeof allScenes === 'object' ? [allScenes] : [];
            }

            console.log('📦 Total Scenes Loaded:', allScenes.length);
            console.table(allScenes.map(s => ({
                id: s.scene_id, name: s.name,
                lat: s.latitude, lon: s.longitude,
                hasModel: !!s.buildingModel || !!s.local_glb_uri
            })));

            // ── Step 3: Enrich scenes with building GLB locations from GraphQL ────────
            console.log('\n🔍 Fetching building models from GraphQL API...');
            allScenes.forEach(s => {
                if (s.building_id) {
                    console.log(`  - ${s.name}: building_id=${s.building_id}, buildingModel=${s.buildingModel || 'NOT SET'}`);
                }
            });

            const cookieData = parseTwinupCookie(twinupCookie);
            try {
                const graphqlRes  = await fetch(`${getBaseUrl()}/sim`, {
                    method:  'POST',
                    headers: { 'authorization': `Bearer ${cookieData.access_token}`, 'content-type': 'application/json' },
                    body:    JSON.stringify({ query: 'query { Building { id glblocation} }' })
                });
                const graphqlData = await graphqlRes.json();
                console.log('\n✅ GraphQL API Response received');

                if (graphqlData.data?.Building?.length) {
                    console.log(`📦 Buildings found: ${graphqlData.data.Building.length}`);
                    const buildingMap = Object.fromEntries(
                        graphqlData.data.Building.map(b => {
                            console.log(`  📌 ${b.id} -> ${b.glblocation}`);
                            return [b.id, b.glblocation];
                        })
                    );
                    let updatedCount = 0;
                    allScenes = allScenes.map(scene => {
                        if (scene.building_id && buildingMap[scene.building_id]) {
                            updatedCount++;
                            return { ...scene, buildingModel: buildingMap[scene.building_id] };
                        }
                        return scene;
                    });
                    console.log(`\n✅ Updated ${updatedCount} / ${allScenes.length} scenes with building models`);
                } else {
                    console.warn('⚠️  No building data found in GraphQL response');
                }
            } catch (err) {
                console.error('❌ Failed to fetch building models from GraphQL:', err.message);
                // Non-fatal – continue with original scenes
            }

            console.log('\n📊 Final scenes after building model update:');
            allScenes.forEach(s => {
                if (s.building_id) {
                    console.log(`  ✓ ${s.name}: buildingModel=${s.buildingModel || 'NOT SET'}`);
                }
            });

            // ── Flatten attributes[] → top-level fields on every scene so that
            //    scale / rotation / angularRotation etc. are available when a card is
            //    clicked and window.loadScene(dataValue) is called directly.
            allScenes = allScenes.map(scene => {
                if (!Array.isArray(scene.attributes) || !scene.attributes.length) return scene;
                const attrMap = scene.attributes.reduce((map, attr) => {
                    if (attr?.name !== undefined) map[attr.name] = attr.value;
                    return map;
                }, {});
                const merged = { ...scene, ...attrMap };
                if (merged.latitude)  merged.latitude  = parseFloat(merged.latitude);
                if (merged.longitude) merged.longitude = parseFloat(merged.longitude);
                if (merged.height)    merged.height    = parseFloat(merged.height);
                if (!merged.buildingModel && merged.building_glb)          merged.buildingModel = merged.building_glb;
                if (!merged.buildingModel && merged.buildingModelLocation)  merged.buildingModel = merged.buildingModelLocation;
                if (!merged.buildingid    && merged.buildingId)            merged.buildingid    = merged.buildingId;
                return merged;
            });

            setSceneList(allScenes);
            setSceneItemsDom(sceneItems(sceneType, selectedFolder, allScenes));
            window.twinWorldSceneList = allScenes;

            // ── Step 4a: Single-scene deep-link mode ─────────────────────────────────
            const buildingGlbMap = {};
            if (searchSceneId) {
                let scene = allScenes.find(s => s.scene_id === searchSceneId || s.id === searchSceneId) ?? null;
                // Flatten attributes[] → top-level fields (same as Step 4b billboard path)
                if (scene && Array.isArray(scene.attributes) && scene.attributes.length) {
                    const scene_attr = scene.attributes.reduce((map, attr) => {
                        if (attr?.name !== undefined) map[attr.name] = attr.value;
                        return map;
                    }, {});
                    scene = { ...scene, ...scene_attr };
                    // Keep numeric types for lat/lon/height
                    if (scene.latitude)  scene.latitude  = parseFloat(scene.latitude);
                    if (scene.longitude) scene.longitude = parseFloat(scene.longitude);
                    if (scene.height)    scene.height    = parseFloat(scene.height);
                    // Derive convenience aliases
                    if (!scene.buildingModel && scene.building_glb)         scene.buildingModel = scene.building_glb;
                    if (!scene.buildingModel && scene.buildingModelLocation) scene.buildingModel = scene.buildingModelLocation;
                    if (!scene.buildingid    && scene.buildingId)           scene.buildingid    = scene.buildingId;
                }
                if (scene) {
                    if (scene.BuildingScene) {
                        document.querySelector('twinworld-app').shadowRoot.querySelector('main').innerHTML =
                            '<p class="MuiTypography-root MuiTypography-body1 css-s9wadb-MuiTypography-root">TwinUp Building Application</p>';
                        window.location.href = `twinup://ImportApp?access_token=${accessUid}wsid=${scene.scene_id}`;
                    } else {
                        buildingDetails(
                            cookieData.access_token,
                            scene.buildingid,
                            scene.buildingS3Location,
                            scene.structuralSystemS3Location
                        ).then((buildingData) => {
                            const buildingS3Location = buildingData.data.Building[0].s3location;
                            const idx = buildingS3Location.indexOf(',');
                            if (buildingS3Location !== '') {
                                const formattedBuildingS3Loc = idx !== -1 ? buildingS3Location.substring(0, idx) : buildingS3Location;
                                const formattedThumbnailLoc  = idx !== -1 ? buildingS3Location.substring(idx + 1) : '';
                                getBuildingUriGlb(scene.buildingid, formattedBuildingS3Loc, formattedThumbnailLoc).then(
                                    (buildingGlbUri) => {
                                        buildingGlbMap[scene.buildingid] = buildingGlbUri.glb;
                                        scene.local_glb_uri       = buildingGlbUri.glb;
                                        scene.local_thumbnail_uri = buildingGlbUri.thumbnail;
                                        viewer.clock.onTick.removeEventListener(window.applyGlobeSpin);
                                        setTimeout(() => {
                                            if (Cesium.GoogleMaps?.defaultApiKey) {
                                                try {
                                                    Cesium.Cesium3DTileset.fromUrl(
                                                        `https://tile.googleapis.com/v1/3dtiles/root.json?key=${Cesium.GoogleMaps.defaultApiKey}`,
                                                        { showCreditsOnScreen: true, maximumScreenSpaceError: 24, maximumMemoryUsage: 512 }
                                                    ).then(tileset => {
                                                        window.viewer.scene.primitives.add(tileset);
                                                        window.googlePhotoRealisticTileset = tileset;
                                                        console.log('✅ Google 3D Tiles loaded');
                                                    }).catch(err => console.warn('Google 3D Tiles unavailable:', err.message));
                                                } catch (err) {
                                                    console.warn('Google 3D Tiles unavailable:', err.message);
                                                }
                                            }
                                            console.log('Loading scene from community frame::', scene);
                                            document.querySelector('twinworld-app').shadowRoot.querySelector('#robot-scanner').style.opacity = 0;
                                            if (typeof window.startWeatherAirQualityWidget === 'function' && scene.latitude && scene.longitude) {
                                                window.startWeatherAirQualityWidget(scene.latitude, scene.longitude);
                                            }
                                            // Track the currently-loaded project so location-intel POI
                                            // searches can drop a "project" pin the user can click to fly back.
                                            window.currentProjectScene = {
                                                name: scene.name,
                                                latitude: scene.latitude,
                                                longitude: scene.longitude
                                            };
                                        }, 100);
                                    }
                                );
                            }
                        });
                    }
                }
            } else {
                // ── Step 4b: Globe overview – create billboard for every scene ─────────
                document.querySelector('twinworld-app').shadowRoot.querySelector('.blackbg').style.display = 'none';

                const allScenesData = allScenes
                    .map((scene, index) => {
                        console.log(`\n${'═'.repeat(56)}`);
                        console.log(`🔍 Processing Scene ${index + 1}/${allScenes.length}`);
                        console.log(`${'═'.repeat(56)}`);

                        let baseScene = {
                            id: scene?.id, name: scene?.name, building_id: scene?.building_id,
                            description: scene?.description, assetName: scene?.name,
                            latitude: scene?.latitude, longitude: scene?.longitude,
                            boundingBox: scene?.boundingBox, buildingModel: scene?.buildingModel,
                            local_glb_uri: scene?.local_glb_uri, sceneImage: scene?.sceneImage,
                            relative_height: scene?.relative_height || 0,
                            rotationX: scene?.rotationX || 0, rotationY: scene?.rotationY || 0, rotationZ: scene?.rotationZ || 0,
                            twinProvider: scene?.twinProvider || 'cesium',
                            itwinConfig: scene?.itwinConfig || null
                        };

                        let scene_attr = {};
                        if (scene?.attributes && Array.isArray(scene.attributes)) {
                            scene_attr = scene.attributes.reduce((map, attr) => {
                                if (attr?.name !== undefined) map[attr.name] = attr.value;
                                return map;
                            }, {});
                        } else {
                            console.log(`⚠️  Scene "${scene?.name || 'Unknown'}" missing scene-level attributes`);
                        }

                        baseScene = { ...baseScene, ...scene_attr };

                        // Always ensure latitude/longitude are numbers (attributes spread may overwrite with strings)
                        baseScene.latitude  = parseFloat(baseScene.latitude  ?? scene?.latitude)  || 0;
                        baseScene.longitude = parseFloat(baseScene.longitude ?? scene?.longitude) || 0;

                        if (scene_attr.twin_provider?.toLowerCase() === 'bentley') {
                            baseScene.twinProvider = 'itwin';
                            baseScene.sceneImage   = scene_attr.sceneImage;
                            baseScene.itwinConfig  = {
                                clientId:  scene_attr.client_id,
                                secret:    scene_attr.client_secret,
                                iModelId:  scene_attr.imodel_id,
                                iTwinId:   scene_attr.itwin_id,
                                ionToken:  scene_attr.ionToken ||
                                    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI5YjdmZGI4MC1hNDI2LTQ4NWYtODMyZS1lZGM3NTVlZjMzMDciLCJpZCI6MTE0MDMwLCJpYXQiOjE2NjgzOTg3ODF9.-in98k0Qp44mvZVsxYmNV_Dvk-T9L0gZkenNq9Ex7Qg'
                            };
                        }
                        return baseScene;
                    })
                    .filter(Boolean);

                console.log(`\n📊 SCENE LOADING SUMMARY: ${allScenes.length} fetched, ${allScenesData.length} processed`);

                // ── Shared HTML tooltip for billboard hover ───────────────────────────
                // Injected directly into document.body so it always renders above the
                // Cesium canvas and shadow DOM regardless of z-index stacking.
                const billboardTooltip = (() => {
                    const existing = document.getElementById('scene-billboard-tooltip');
                    if (existing) return existing;
                    const el = document.createElement('div');
                    el.id = 'scene-billboard-tooltip';
                    el.style.cssText = [
                        'position:fixed',
                        'z-index:2147483646',
                        'background:rgba(22,33,62,0.97)',
                        'color:#fff',
                        'padding:7px 14px',
                        'border-radius:8px',
                        'font-size:13px',
                        'font-weight:500',
                        'font-family:"Suisse Intl",sans-serif',
                        'pointer-events:none',
                        'display:none',
                        'border:1px solid rgba(243,111,75,0.55)',
                        'box-shadow:0 4px 14px rgba(0,0,0,0.55)',
                        'max-width:220px',
                        'white-space:nowrap',
                        'letter-spacing:0.3px',
                    ].join(';');
                    document.body.appendChild(el);
                    return el;
                })();

                allScenesData.forEach((sceneData, index) => {
                    console.log(`\n🏗️  Creating Billboard ${index + 1}/${allScenesData.length} — ${sceneData.name}`);
                    window.showAlert('Loading scenes...');
                    try {
                        const sceneEntity = viewer.entities.add({
                            position: Cesium.Cartesian3.fromDegrees(sceneData.longitude, sceneData.latitude),
                            billboard: {
                                height: 50, width: 50,
                                image: sceneData.sceneImage
                                    ? `${getCloudfrontS3()}/${sceneData.sceneImage.split('/').splice(-2).join('/')}`
                                    : 'https://dtuas2sql0dg4.cloudfront.net/build/cesium/Widgets/Images/ImageryProviders/bingAerial.png',
                                outlineColor: new Cesium.Color(243/255, 111/255, 75/255, 1),
                                outlineWidth: 3,
                                verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                                pixelOffset: new Cesium.Cartesian2(0, -15)
                            },
                            label: {
                                text: sceneData.name, font: '0.5em Suisse Intl',
                                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                                horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
                                verticalOrigin: Cesium.VerticalOrigin.CENTER,
                                fillColor: Cesium.Color.GHOSTWHITE, showBackground: true,
                                backgroundColor: Cesium.Color.DARKSLATEGREY.withAlpha(0.8),
                                backgroundPadding: new Cesium.Cartesian2(8, 12),
                                pixelOffset: new Cesium.Cartesian2(40, 0),
                                disableDepthTestDistance: Number.POSITIVE_INFINITY
                            }
                        });
                        sceneEntity.label.scale = 0;

                        const scenePointer = viewer.entities.add({
                            position: Cesium.Cartesian3.fromDegrees(sceneData.longitude, sceneData.latitude),
                            point: {
                                color: Cesium.Color.CORNFLOWERBLUE, pixelSize: 0,
                                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                                disableDepthTestDistance: Number.POSITIVE_INFINITY
                            }
                        });
                        sceneEntity.refPoint = scenePointer.point;

                        const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

                        // Per-entity hover flag — avoids unreliable Cesium property === comparison
                        let isHovered = false;

                        // Hover: show HTML tooltip + scale billboard
                        handler.setInputAction((movement) => {
                            if (window.drone) return;
                            const picked = viewer.scene.pick(movement.endPosition);
                            if (Cesium.defined(picked) && picked.id === sceneEntity) {
                                if (!isHovered) {
                                    isHovered = true;
                                    sceneEntity.billboard.scale        = 1.5;
                                    sceneEntity.billboard.outlineColor = Cesium.Color.FORESTGREEN;
                                }
                                // Position tooltip near cursor using canvas bounding rect
                                const canvas = viewer.scene.canvas;
                                const rect   = canvas.getBoundingClientRect();
                                const tx     = rect.left + movement.endPosition.x + 16;
                                const ty     = rect.top  + movement.endPosition.y - 36;
                                billboardTooltip.textContent   = sceneData.name || 'Unnamed Scene';
                                billboardTooltip.style.left    = tx + 'px';
                                billboardTooltip.style.top     = ty + 'px';
                                billboardTooltip.style.display = 'block';
                            } else if (isHovered) {
                                isHovered = false;
                                sceneEntity.billboard.scale        = 1.0;
                                sceneEntity.billboard.outlineColor = new Cesium.Color(243/255, 111/255, 75/255, 1);
                                billboardTooltip.style.display = 'none';
                            }
                        }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

                        // Click: load the scene
                        handler.setInputAction((clickEvent) => {
                            // Skip if a building is already loaded in the viewer
                            if (window.drone) return;
                            const picked = viewer.scene.pick(clickEvent.position);
                            if (!Cesium.defined(picked) || picked.id !== sceneEntity) return;
                            console.log('🎯 PHASE 2: Scene Selected —', sceneData.name);

                            // Hide tooltip immediately on click
                            billboardTooltip.style.display = 'none';
                            isHovered = false;

                            sceneEntity.billboard.scale   = 0;
                            sceneEntity.label.scale       = 0;
                            sceneEntity.refPoint.pixelSize = 0;

                            window.sceneId = sceneData.id;

                            const twinApp = document.querySelector('twinworld-app');
                            if (!twinApp) { console.error('❌ twinworld-app not found'); return; }

                            // Create analyze menu on first click
                            const muiStackRoot = document.querySelector('.MuiStack-root');
                            if (!searchSceneId && muiStackRoot && !muiStackRoot.parentNode.querySelector('.createAnalyzeMenu')) {
                                const el = document.createElement('div');
                                el.classList.add('createAnalyzeMenu');
                                muiStackRoot.parentNode.append(el);
                                twinApp.shadowRoot.querySelector('.cesium-geocoder-input')?.style && (twinApp.shadowRoot.querySelector('.cesium-geocoder-input').style.transform = 'translateY(30px)');
                                twinApp.shadowRoot.querySelector('[aria-label="search"]')?.style && (twinApp.shadowRoot.querySelector('[aria-label="search"]').style.transform = 'translateX(30px) translateY(-10px)');
                            }

                            sceneData.latitude  = parseFloat(sceneData.latitude);
                            sceneData.longitude = parseFloat(sceneData.longitude);

                            if (typeof window.startWeatherAirQualityWidget === 'function') {
                                window.startWeatherAirQualityWidget(sceneData.latitude, sceneData.longitude);
                            }
                            // Track the currently-loaded project so location-intel POI
                            // searches can drop a "project" pin the user can click to fly back.
                            window.currentProjectScene = {
                                name: sceneData.name,
                                latitude: sceneData.latitude,
                                longitude: sceneData.longitude
                            };

                            const boundingBox = parseBoundingBox(sceneData.boundingBox);
                            if (!boundingBox) { console.error('❌ Failed to parse boundingBox'); return; }

                            const bboxArray = Array.isArray(boundingBox)
                                ? boundingBox
                                : [boundingBox.west, boundingBox.south, boundingBox.east, boundingBox.north];

                            if (bboxArray.length !== 4 || bboxArray.some(v => typeof v !== 'number' || isNaN(v))) {
                                console.error('❌ Invalid bounding box values:', bboxArray); return;
                            }

                            sceneData.boundingBox = JSON.stringify({
                                west: bboxArray[0], south: bboxArray[1], east: bboxArray[2], north: bboxArray[3]
                            });

                            const twinProvider = sceneData.twinProvider || 'cesium';
                            const robotScanner = twinApp.shadowRoot.querySelector('#robot-scanner');
                            if (robotScanner) robotScanner.style.opacity = 0;

                            if (twinProvider === 'itwin') {
                                console.log('🔀 Loading iTwin scene:', sceneData.name);
                                window.loadScene(sceneData);
                            } else {
                                console.log('🔀 Loading Cesium scene:', sceneData.name);

                                // Load Google Photorealistic 3D Tiles (optional / fire-and-forget)
                                if (Cesium.GoogleMaps?.defaultApiKey) {
                                    Cesium.Cesium3DTileset.fromUrl(
                                        `https://tile.googleapis.com/v1/3dtiles/root.json?key=${Cesium.GoogleMaps.defaultApiKey}`,
                                        {
                                            showCreditsOnScreen: true,
                                            maximumScreenSpaceError: 24, maximumMemoryUsage: 512,
                                            skipLevelOfDetail: false, baseScreenSpaceError: 1024,
                                            skipScreenSpaceErrorFactor: 16, skipLevels: 1,
                                            immediatelyLoadDesiredLevelOfDetail: false, loadSiblings: false,
                                            cullWithChildrenBounds: true, cullRequestsWhileMoving: true,
                                            cullRequestsWhileMovingMultiplier: 60.0, preloadWhenHidden: false,
                                            preferLeaves: false, dynamicScreenSpaceError: true,
                                            dynamicScreenSpaceErrorDensity: 0.00278, dynamicScreenSpaceErrorFactor: 4.0,
                                            dynamicScreenSpaceErrorHeightFalloff: 0.25,
                                            foveatedScreenSpaceError: true, foveatedConeSize: 0.1,
                                            foveatedMinimumScreenSpaceErrorRelaxation: 0.0
                                        }
                                    ).then(tileset => {
                                        window.viewer.scene.primitives.add(tileset);
                                        window.googlePhotoRealisticTileset = tileset;
                                        console.log('✅ Google 3D Tiles loaded');
                                    }).catch(err => console.warn('Google 3D Tiles unavailable:', err.message));
                                }

                                console.log(' 1 Calling window.loadScene with:', sceneData);
                                window.loadScene(sceneData);

                                if (window.viewer?.scene && window.viewer?.camera) {
                                    window.viewer.scene.mode = Cesium.SceneMode.SCENE3D;
                                } else {
                                    console.error('❌ Cannot set scene mode - viewer not ready');
                                }
                            }

                            // Reposition UI chrome
                            const compass     = twinApp.shadowRoot.querySelector('.compass');
                            const navControls = twinApp.shadowRoot.querySelector('.navigation-controls');
                            if (compass)     compass.style.right     = '100px';
                            if (navControls) navControls.style.right = '130px';

                            setTimeout(() => {
                                const mailbox = twinApp.shadowRoot.querySelector('[aria-label="mailbox folders"]');
                                const geocoder = twinApp.shadowRoot.querySelector('.cesium-viewer-geocoderContainer');
                                if (mailbox)  mailbox.style.background  = 'black';
                                if (geocoder) geocoder.style.display     = 'none';
                            }, 4000);

                        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

                    } catch (e) {
                        console.error(`❌ Failed to create billboard for scene "${sceneData.name}":`, e.message);
                    }
                });

                window.showAlert('All scenes loaded');
                if (window.applyGlobeSpinEvent)   window.applyGlobeSpinEvent();
                if (window.viewer && window.applyGlobeSpin) window.viewer.clock.onTick.removeEventListener(window.applyGlobeSpin);
                window.globeRotation = false;

                console.log('\n🎉 All Scenes Rendered —', new Date().toISOString());

                setTimeout(() => {
                    document.querySelector('twinworld-app').shadowRoot.querySelector('.MuiSnackbar-root button[title="Close"]')?.click();
                }, 1500);
                setTimeout(() => {
                    document.querySelector('twinworld-app').shadowRoot.querySelector('.blackbg').style.display = 'none';
                    document.querySelector('twinworld-app').shadowRoot.querySelector('.loaderbg').style.display = 'block';
                    setTimeout(() => {
                        document.querySelector('twinworld-app').shadowRoot.querySelector('.loaderbg').style.display = 'none';
                    }, 3000);
                }, 2000);
            }
        })().catch(error => {
            console.error('❌ Failed to initialize viewer or load scenes:', error);
        });
        // eslint-disable-next-line
    setTimeout(() => {
            const twinworldApp = document.querySelector('twinworld-app');
            const twinworldAppObserver = new MutationObserver(function (mutations) {
                mutations.forEach(function (mutation) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'search_term') {
                        const rawSearchTerm = mutation.target.getAttribute('search_term');
                        console.log('search term ::', rawSearchTerm);

                        // Parse optional [ACTION] / [ADVICE] prefix injected by the chatbox mode toggle
                        const modeMatch = rawSearchTerm?.match(/^\[(ACTION|ADVICE)\]\s*/i);
                        const mode = modeMatch ? modeMatch[1].toLowerCase() : 'action';
                        const searchTerm = modeMatch ? rawSearchTerm.slice(modeMatch[0].length).trim() : rawSearchTerm;
                        console.log('chat mode ::', mode, '| clean prompt ::', searchTerm);
                        document.querySelector('twinworld-app').shadowRoot.querySelector('#cesiumContainer').style.width =
                            'calc(100vw - 64px)';
                        setTimeout(() => {
                            if (document.querySelector('twinworld-app').shadowRoot.querySelector('.cesium-sceneModePicker-button2D')) {
                                document
                                    .querySelector('twinworld-app')
                                    .shadowRoot.querySelector('.cesium-sceneModePicker-button2D')
                                    .click();
                                setTimeout(() => {
                                    document
                                        .querySelector('twinworld-app')
                                        .shadowRoot.querySelectorAll('.cesium-sceneModePicker-visible')[0]
                                        .click();
                                    // document.querySelector('twinworld-app').shadowRoot.querySelector('#modelOnMap').click();
                                    
                                    // ✅ Load Google Photorealistic 3D Tiles (Google Maps Platform approach)
                                    if (Cesium.GoogleMaps && Cesium.GoogleMaps.defaultApiKey) {
                                        console.log('🗺️  Loading Google 3D Tiles (search term handler)');
                                        console.log('   API Key (first 10 chars):', Cesium.GoogleMaps.defaultApiKey.substring(0, 10) + '...');
                                        
                                        try {
                                            Cesium.Cesium3DTileset.fromUrl(
                                                `https://tile.googleapis.com/v1/3dtiles/root.json?key=${Cesium.GoogleMaps.defaultApiKey}`,
                                                {
                                                    showCreditsOnScreen: true,
                                                    maximumScreenSpaceError: 24,
                                                    maximumMemoryUsage: 512,
                                                    skipLevelOfDetail: false,
                                                    baseScreenSpaceError: 1024,
                                                    skipScreenSpaceErrorFactor: 16,
                                                    skipLevels: 1,
                                                    immediatelyLoadDesiredLevelOfDetail: false,
                                                    loadSiblings: false,
                                                    cullWithChildrenBounds: true,
                                                    cullRequestsWhileMoving: true,
                                                    cullRequestsWhileMovingMultiplier: 60.0,
                                                    preloadWhenHidden: false,
                                                    preferLeaves: false,
                                                    dynamicScreenSpaceError: true,
                                                    dynamicScreenSpaceErrorDensity: 0.00278,
                                                    dynamicScreenSpaceErrorFactor: 4.0,
                                                    dynamicScreenSpaceErrorHeightFalloff: 0.25,
                                                    foveatedScreenSpaceError: true,
                                                    foveatedConeSize: 0.1,
                                                    foveatedMinimumScreenSpaceErrorRelaxation: 0.0
                                                }
                                            ).then(tileset => {
                                                window.viewer.scene.primitives.add(tileset);
                                                window.googlePhotoRealisticTileset = tileset;
                                                console.log('✅ Google 3D Tiles loaded (search term handler)');
                                            }).catch(err => {
                                                console.warn('Google 3D Tiles unavailable (search handler):', err.message);
                                            });
                                        } catch (error) {
                                            console.error('❌ Failed to create Google 3D Tiles (search handler):', error);
                                            console.error('   Error stack:', error.stack);
                                        }
                                    }
                                    
                                    /*if (searchTerm.toLowerCase().indexOf('go') >= 0) {
                                        mutation.target.setAttribute('search_response', 'Going to location');
                                    }*/
                                }, 1000);
                            }
                        }, 1000);

                        handleSubmit(searchTerm, mode).then((searchResponse) => {
                            console.log('Search response = ', searchResponse, mutation.target);
                            const prevResponse = mutation.target.getAttribute('search_response');
                            const currResponse = prevResponse === searchResponse ? searchResponse + ' ' : searchResponse;
                            mutation.target.setAttribute('search_response', currResponse);
                        });

                        // console.log('search function', mutation.target.setAttribute('search_response', ''));
                    }
                });
            });
            // Track observer for cleanup
            mutationObserverRef.current = twinworldAppObserver;
            twinworldAppObserver.observe(twinworldApp, {
                attributes: true
            });
        }, 3000);

        // Cleanup function to prevent memory leaks
        return () => {
            // Destroy all event handlers
            eventHandlersRef.current.forEach((handler) => {
                if (handler && !handler.isDestroyed()) {
                    handler.destroy();
                }
            });
            eventHandlersRef.current = [];

            // Disconnect mutation observer
            if (mutationObserverRef.current) {
                mutationObserverRef.current.disconnect();
                mutationObserverRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ maxWidth: 1200, flexGrow: 1, zIndex: 1001 }}>
            <AliceCarousel
                mouseTracking
                // items={sceneItems(sceneList)}
                items={sceneItemsDom}
                responsive={responsive}
                controlsStrategy="alternate"
                disableDotsControls="true"
                renderPrevButton={() => {
                    return (
                        <p
                            style={{
                                position: 'absolute',
                                left: '-3px',
                                top: '23px',
                                transform: 'scale(0.8) translateY(-10px)',
                                cursor: 'pointer'
                            }}
                            className="p-4 absolute left-0 top-0"
                        >
                            <img alt="left" src="https://twinworld-component.s3.amazonaws.com/build/utils/lib/LeftArrow.png" />
                        </p>
                    );
                }}
                renderNextButton={() => {
                    return (
                        <p
                            style={{
                                position: 'absolute',
                                left: '465px',
                                top: '23px',
                                transform: 'scale(0.8) translateY(-10px)',
                                cursor: 'pointer'
                            }}
                            className="p-4 absolute right-0 top-0"
                        >
                            <img alt="right" src="https://twinworld-component.s3.amazonaws.com/build/utils/lib/RightArrow.png" />
                        </p>
                    );
                }}
            />
        </Box>
    );
}

export default Scenes;
